import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, MapPin, RefreshCw, TrendingUp, TrendingDown, Link as LinkIcon, Coins, AlertTriangle, Play, Package, PlusCircle, Trash2, X, CheckCircle, Circle, ChevronDown, ChevronUp, ArrowUpRight, ArrowDownRight, Minus, Clock, Beaker, Target, Copy, Monitor, Smartphone, Tablet, Globe, BarChart3, Eye, MousePointer2, Timer, Users, RotateCcw, ExternalLink } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell, BarChart, Bar } from 'recharts';
import HistoryCalendar from '../components/HistoryCalendar';
import PdfDownloadButton from '../components/PdfDownloadButton';
import ScheduleConfig from '../components/ScheduleConfig';
import LocationSelector from '../components/LocationSelector';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const AI_ENGINE_COLORS = {
  'ChatGPT': '#10B981',
  'Perplexity': '#22B8CF',
  'Google AI': '#F59E0B',

  'Grok': '#EF4444',
  'Google AI Overviews': '#EC4899',
  'Google AI Mode': '#84CC16',
  'Bing Copilot': '#F97316',
  'You.com': '#7C3AED'
};

const TIME_FILTERS = [
  { labelKey: '7_дней', days: 7 },
  { labelKey: '30_дней', days: 30 },
  { labelKey: '90_дней', days: 90 },
  { labelKey: '1_год', days: 365 }
];

const PRIORITY_STYLES = {
  high: { bg: 'bg-red-500', labelKey: 'высокий' },
  medium: { bg: 'bg-yellow-500', labelKey: 'средний' },
  low: { bg: 'bg-green-500', labelKey: 'низкий' },
};

const VERDICT_STYLES = {
  active: { bg: 'bg-blue-100 text-blue-800', labelKey: 'активна' },
  checking: { bg: 'bg-yellow-100 text-yellow-800', labelKey: 'проверка_статус' },
  confirmed: { bg: 'bg-green-100 text-green-800', labelKey: 'подтверждена' },
  refuted: { bg: 'bg-red-100 text-red-800', labelKey: 'опровергнута' },
  neutral: { bg: 'bg-slate-100 text-slate-700', labelKey: 'нейтрально' },
  too_early: { bg: 'bg-orange-100 text-orange-800', labelKey: 'рано_проверять' },
  abandoned: { bg: 'bg-slate-200 text-slate-500', labelKey: 'отклонена_статус' },
  declined: { bg: 'bg-slate-200 text-slate-500', labelKey: 'не_принята' },
  closed: { bg: 'bg-slate-200 text-slate-500', labelKey: 'закрыта' },
};

/* ─── Audit v3 Constants ─── */
const AUDIT_BLOCKS = {
  site_foundation: { label: 'Site Foundation', color: '#3B82F6' },
  page_quality: { label: 'Page Quality', color: '#8B5CF6' },
  ai_ready: { label: 'AI-Ready Content', color: '#F59E0B' },
  ai_authority: { label: 'AI Authority', color: '#EF4444' },
};

const WHO_BADGES = {
  developer: { label: 'Developer', cls: 'bg-blue-100 text-blue-700' },
  content: { label: 'Content', cls: 'bg-amber-100 text-amber-700' },
  webmaster: { label: 'Webmaster', cls: 'bg-purple-100 text-purple-700' },
};

const FIX_HINTS = {
  T01: 'Install an SSL certificate and redirect all HTTP traffic to HTTPS.',
  T02: 'Add header: Strict-Transport-Security: max-age=31536000; includeSubDomains',
  T03: 'Add a Content-Security-Policy header to your server configuration.',
  T04: 'Add header: X-Content-Type-Options: nosniff',
  T05: 'Add header: X-Frame-Options: DENY or SAMEORIGIN',
  T06: 'Add header: Referrer-Policy: strict-origin-when-cross-origin',
  T07: 'Create a robots.txt file at your site root: https://yoursite.com/robots.txt',
  T08: 'Remove "Disallow: /" from robots.txt to allow crawling.',
  T09: 'Create an XML sitemap and make it accessible. Most CMS auto-generate one.',
  T10: 'Add "Sitemap: https://yoursite.com/sitemap.xml" to robots.txt.',
  T11: 'Add <link rel="canonical" href="CURRENT_URL"> to your page <head>.',
  T12: 'Remove <meta name="robots" content="noindex"> if the page should be indexed.',
  T13: 'Fix server errors causing non-200 status codes.',
  T14: 'Reduce redirect chains — each URL should resolve in 0-1 redirects.',
  T15: 'Add lang attribute: <html lang="en">',
  T16: 'Add <meta charset="UTF-8"> to your page <head>.',
  T17: 'Optimize Largest Contentful Paint: compress images, use CDN, preload hero image, reduce server response time.',
  T18: 'Reduce Interaction to Next Paint: break up long JavaScript tasks, use web workers, defer non-critical scripts.',
  T19: 'Fix Cumulative Layout Shift: set explicit width/height on images and ads, avoid injecting content above existing content.',
  T20: 'Improve Performance Score: optimize images (WebP/AVIF), enable caching, minify CSS/JS, eliminate render-blocking resources.',
  T23: 'Make your page mobile-friendly: use responsive design, set viewport meta tag, ensure tap targets are ≥ 48px.',
  T21: 'Enable gzip or brotli compression in your server config (nginx/Apache).',
  T22: 'Add <link rel="preconnect"> or <link rel="preload"> for critical resources.',
  T24: 'Add <meta name="viewport" content="width=device-width, initial-scale=1">',
  T25: 'Use clean, readable URLs without excessive query parameters.',
  T27: 'Add BreadcrumbList JSON-LD schema to help search engines understand site structure.',
  T28: 'Add a favicon: <link rel="icon" href="/favicon.ico">',
  T30: 'Add at least one JSON-LD structured data block. Start with Organization schema.',
  T31: 'Fix JSON-LD syntax errors — validate at search.google.com/test/rich-results.',
  T32: 'Add Organization schema: {"@type":"Organization","name":"...","url":"...","logo":"..."}',
  T33: 'Add WebSite schema with SearchAction for sitelinks search box.',
  T38: 'Add sameAs links in Organization schema pointing to your social profiles.',
  P01: 'Add a descriptive <title> tag to your page <head>.',
  P02: 'Adjust title to 30-60 characters for optimal search display.',
  P03: 'Add <meta name="description" content="..."> with a compelling page summary.',
  P04: 'Adjust meta description to 120-160 characters.',
  P05: 'Add <meta property="og:title"> for social media sharing.',
  P06: 'Add <meta property="og:description"> for social media sharing.',
  P07: 'Add <meta property="og:image"> with an image URL (1200×630px recommended).',
  P08: 'Add <meta property="og:url"> with the canonical URL of this page.',
  P09: 'Add <meta name="twitter:card" content="summary_large_image">',
  P11: 'Add exactly one <h1> heading describing the main topic of your page.',
  P12: 'Adjust H1 to 20-70 characters.',
  P13: 'Add at least 2 <h2> subheadings to structure your content.',
  P14: 'Fix heading hierarchy — go H1→H2→H3 without skipping levels.',
  P18: 'Expand content to at least 800 words for better search visibility.',
  P19: 'Write longer paragraphs (20+ words average) for better readability.',
  P20: 'Increase text-to-HTML ratio — reduce template code, add more content.',
  P26: 'Add descriptive alt text to all images: <img alt="Description of image">',
  P28: 'Add width and height attributes to images to prevent layout shift.',
  P29: 'Add loading="lazy" to images below the fold.',
  P30: 'Convert images to WebP or AVIF format for faster loading.',
  P31: 'Add more internal links connecting to other pages on your site.',
  P32: 'Add 2+ external links to authoritative sources.',
  P33: 'Use descriptive anchor text instead of "click here" or "read more".',
  P34: 'Add <nav> and <footer> elements with navigation links.',
  A01: 'Allow GPTBot in robots.txt — remove any blocking rules for GPTBot.',
  A02: 'Allow ChatGPT-User in robots.txt — remove any blocking rules.',
  A03: 'Allow ClaudeBot in robots.txt to let Claude cite your content.',
  A04: 'Allow PerplexityBot in robots.txt to let Perplexity cite your content.',
  A05: 'Allow Google-Extended in robots.txt for Google AI features.',
  A06: 'Allow Bingbot access (used by Copilot) in robots.txt.',
  A07: 'Review robots.txt and allow AI bots that you want to cite your content.',
  A08: 'Remove or reduce Crawl-delay to under 5 seconds for AI bots.',
  A09: 'Add FAQPage schema with at least 3 Q&A pairs.',
  A10: 'Add HowTo schema with step-by-step instructions.',
  A11: 'Add Speakable schema to mark content suitable for voice assistants.',
  A16: 'Add more schema types: Organization, FAQ, Article, BreadcrumbList, etc.',
  A17: 'Add an FAQ section with clearly formatted questions and answers.',
  A18: 'Use question-style headings (ending with "?") for AI discoverability.',
  A20: 'Add clear definitions for key terms used on the page.',
  A21: 'Add structured lists (<ul>/<ol>) to organize information.',
  A22: 'Add comparison tables with clear headers.',
  A23: 'Add step-by-step instructions with numbered steps.',
  G01: 'Create /llms.txt — a markdown file describing your site for AI. See llmstxt.org.',
  G02: 'Create /llms-full.txt with detailed content for AI reference.',
  G03: 'Ensure XML sitemap is accessible and contains 10+ URLs.',
  G04: 'Add a sameAs link to your Wikipedia or Wikidata page in Organization schema.',
  G05: 'Add social media profile links (LinkedIn, Twitter, Facebook, etc.).',
  G06: 'Add an accessible About page with company/team information.',
  G07: 'Add at least 2 contact methods: phone, email, or physical address.',
  G10: 'Use multiple content formats: text, tables, lists, and images.',
  G18: 'Use semantic HTML: <article>, <section>, <aside>, <main> instead of only <div>.',
  G19: 'Wrap images in <figure> with <figcaption> for better semantics.',
  G20: 'Add ARIA landmarks: role="main", role="navigation", role="banner".',
  G22: 'Add a skip navigation link for accessibility.',
  // L4 LLM checks
  P17: 'Rewrite H1 to clearly reflect the main topic keyword of the page.',
  P21: 'Adjust writing to be professional and clear — avoid excessive jargon or overly simple language.',
  P24: 'Add original insights, data, or unique perspective. Avoid generic boilerplate content.',
  P25: 'Add a clear call-to-action: "Get started", "Contact us", "Try free", etc.',
  P27: 'Replace generic alt texts ("image", "photo") with descriptive text explaining image content.',
  A19: 'Start each content section with a direct answer before elaborating. AI engines prefer direct answer patterns.',
  A24: 'Add a summary, conclusion, or TL;DR section at the top or bottom of your content.',
  A25: 'Include specific numbers, statistics, and data points. AI engines prefer citing concrete data.',
  A26: 'Attribute claims to named sources: "According to [Source]..." or cite research.',
  A27: 'Use factual, objective tone. Reduce marketing superlatives and unsupported opinions.',
  A28: 'Write self-contained passages (2-3 sentences) that AI can quote directly without context.',
  A29: 'Mention relevant entities by name: people, companies, products, locations.',
  A30: 'Structure content with clear headings and short paragraphs so AI can easily extract information.',
  G08: 'Make your brand/entity identity clear in the first paragraph. State who you are and what you do.',
  G09: 'Cover the topic comprehensively — address multiple aspects, not just one angle.',
  G11: 'Include original research, proprietary data, or unique case-specific numbers.',
  G12: 'Add expert-level analysis with depth — go beyond surface-level overviews.',
  G13: 'Add 2+ specific case studies or real-world examples illustrating your points.',
  G14: 'Add comparison of alternatives, competitors, or approaches with clear criteria.',
  G15: 'Add author bio with relevant credentials, expertise, and professional links.',
  G16: 'Add trust signals: certifications, awards, testimonials, years in business, client logos.',
  G17: 'Link to authoritative sources: .edu, .gov, research papers, industry leaders.',
  G21: 'Organize content with a clear logical hierarchy: introduction → details → examples → conclusion.',
};

const _auditDiagnosis = (summary) => {
  if (!summary?.blocks) return '';
  const sf = summary.blocks.site_foundation?.score || 0;
  const pq = summary.blocks.page_quality?.score || 0;
  const ar = summary.blocks.ai_ready?.score || 0;
  const aa = summary.blocks.ai_authority?.score || 0;
  const tech = Math.round((sf + pq) / 2);
  const ai = Math.round((ar + aa) / 2);
  if (tech >= 75 && ai >= 75) return 'Your site is well-optimized. Focus on remaining improvements to stay ahead.';
  if (tech < 60) return 'Your site has fundamental issues that need fixing first.';
  if (ai >= 75 && tech < 75) return 'Your content is AI-ready but technical issues are holding you back.';
  if (tech >= 75 && ar < 60) return 'Your site is technically solid but AI engines struggle to read your content.';
  return `Your site scores ${summary.overall_score}% overall. Review the issues below to improve.`;
};

/* ─── CI Badge ─── */
const CiBadge = ({ ci, className = '' }) => {
  if (!ci || (ci.lower === 0 && ci.upper === 0)) return null;
  return (
    <span className={`text-[10px] text-slate-400 ${className}`}>
      CI [{ci.lower}–{ci.upper}%]
    </span>
  );
};

/* ─── Sentiment Mini Bar ─── */
const SentimentBar = ({ breakdown }) => {
  const { t } = useTranslation();
  if (!breakdown) return null;
  const total = (breakdown.positive || 0) + (breakdown.neutral || 0) + (breakdown.negative || 0);
  if (total === 0) return null;
  const pct = v => Math.round((v / total) * 100);
  return (
    <div className="mt-2">
      <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{t('geo.тональность')}</p>
      <div className="flex h-2 w-full overflow-hidden bg-slate-100">
        {breakdown.positive > 0 && <div className="bg-green-400" style={{ width: `${pct(breakdown.positive)}%` }} title={t('geo.позитив', { count: breakdown.positive })} />}
        {breakdown.neutral > 0 && <div className="bg-slate-300" style={{ width: `${pct(breakdown.neutral)}%` }} title={t('geo.нейтрал', { count: breakdown.neutral })} />}
        {breakdown.negative > 0 && <div className="bg-red-400" style={{ width: `${pct(breakdown.negative)}%` }} title={t('geo.негатив', { count: breakdown.negative })} />}
      </div>
      <div className="flex justify-between mt-0.5 text-[9px] text-slate-400">
        <span>+{breakdown.positive}</span>
        <span>~{breakdown.neutral}</span>
        <span>-{breakdown.negative}</span>
      </div>
    </div>
  );
};

/* ─── Position Mini Bar ─── */
const PositionBar = ({ breakdown }) => {
  const { t } = useTranslation();
  if (!breakdown) return null;
  const total = (breakdown.top || 0) + (breakdown.middle || 0) + (breakdown.bottom || 0);
  if (total === 0) return null;
  const pct = v => Math.round((v / total) * 100);
  return (
    <div className="mt-2">
      <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{t('geo.позиция')}</p>
      <div className="flex h-2 w-full overflow-hidden bg-slate-100">
        {breakdown.top > 0 && <div className="bg-emerald-500" style={{ width: `${pct(breakdown.top)}%` }} title={t('geo.топ_title', { count: breakdown.top })} />}
        {breakdown.middle > 0 && <div className="bg-amber-400" style={{ width: `${pct(breakdown.middle)}%` }} title={t('geo.середина_title', { count: breakdown.middle })} />}
        {breakdown.bottom > 0 && <div className="bg-slate-400" style={{ width: `${pct(breakdown.bottom)}%` }} title={t('geo.низ_title', { count: breakdown.bottom })} />}
      </div>
      <div className="flex justify-between mt-0.5 text-[9px] text-slate-400">
        <span>{t('geo.топ_краткий', { count: breakdown.top })}</span>
        <span>{t('geo.серед_краткий', { count: breakdown.middle })}</span>
        <span>{t('geo.низ_краткий', { count: breakdown.bottom })}</span>
      </div>
    </div>
  );
};

const GeoMonitorDetails = () => {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === 'ru' ? 'ru-RU' : 'en-US';
  const { monitorId, geoId } = useParams();
  const navigate = useNavigate();
  const [geoMonitor, setGeoMonitor] = useState(null);
  const [results, setResults] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [progressMessage, setProgressMessage] = useState('');
  const [queueInfo, setQueueInfo] = useState(null);
  const [taskId, setTaskId] = useState(null);
  const [creditBalance, setCreditBalance] = useState(null);
  const [opCosts, setOpCosts] = useState({});
  const [timeFilter, setTimeFilter] = useState(30);
  const [chartMetric, setChartMetric] = useState('visibility');
  const [monitor, setMonitor] = useState(null);
  const [newProduct, setNewProduct] = useState({ name: '', url: '', description: '', price: '', geo: {}, locale: '' });
  const [addingProduct, setAddingProduct] = useState(false);
  const [customQueries, setCustomQueries] = useState([]);
  const [newQuery, setNewQuery] = useState('');
  const [brandResources, setBrandResources] = useState([]);
  const [newBrandResource, setNewBrandResource] = useState('');
  const [submonitors, setSubmonitors] = useState([]);
  const [creatingSubmonitor, setCreatingSubmonitor] = useState(null);
  const [geoAudit, setGeoAudit] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditV3, setAuditV3] = useState(null);
  const [auditV3Loading, setAuditV3Loading] = useState(false);
  const [auditV3Sections, setAuditV3Sections] = useState({ fix_now: true, should_fix: true, nice_to_have: false, working: false, progress: true });
  const [expandedCheck, setExpandedCheck] = useState(null);
  const [pixelStats, setPixelStats] = useState(null);
  const [pixelPeriod, setPixelPeriod] = useState(30);
  const [pixelCodeCopied, setPixelCodeCopied] = useState(false);
  const [pixelSections, setPixelSections] = useState({});
  const [selectedQueryRanking, setSelectedQueryRanking] = useState(null);
  const [resourcesTab, setResourcesTab] = useState('top20');
  const [rankingsHistory, setRankingsHistory] = useState([]);

  // Pixel accordion toggle
  var togglePixelSection = function(key) {
    setPixelSections(function(prev) {
      var next = Object.assign({}, prev);
      next[key] = !prev[key];
      return next;
    });
  };

  // Brand rankings lookup map (Session 31)
  const rankingsMap = React.useMemo(() => {
    const map = {};
    if (results?.query_rankings) {
      results.query_rankings.forEach(r => {
        const key = r.query;
        if (!map[key]) map[key] = {};
        map[key][r.engine] = {
          target_position: r.target_position,
          total_brands: r.total_brands,
          brands: r.brands || []
        };
      });
    }
    return map;
  }, [results]);

  // Fetch rankings history when modal opens (Session 31)
  useEffect(() => {
    if (!selectedQueryRanking || !geoId) {
      setRankingsHistory([]);
      return;
    }
    const fetchHistory = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/geo/${geoId}/rankings-history`);
        setRankingsHistory(res.data || []);
      } catch (e) {
        console.error('[RANKINGS_HISTORY]', e);
        setRankingsHistory([]);
      }
    };
    fetchHistory();
  }, [selectedQueryRanking, geoId]);

  const analysisJustStarted = useRef(false);
  const [hypotheses, setHypotheses] = useState([]);
  const [expandedRec, setExpandedRec] = useState(null);
  const [expandedHyp, setExpandedHyp] = useState(null);
  const [checkingHyp, setCheckingHyp] = useState(null);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const loadData = useCallback(async () => {
    try {
      const [geoRes, resultsRes, historyRes, monitorRes] = await Promise.all([
        axios.get(`${API_URL}/api/geo/${geoId}`),
        axios.get(`${API_URL}/api/geo/${geoId}/results`),
        axios.get(`${API_URL}/api/geo/${geoId}/history?days=${timeFilter}`),
        axios.get(`${API_URL}/api/monitors/${monitorId}`),
      ]);
      setGeoMonitor(geoRes.data);
      setResults(resultsRes.data && Object.keys(resultsRes.data).length > 0 ? resultsRes.data : null);
      setHistory(historyRes.data || []);
      setMonitor(monitorRes.data);
      setCustomQueries(monitorRes.data?.custom_queries || []);
      setBrandResources(monitorRes.data?.brand_resources || []);
      // Load submonitors
      try {
        const subRes = await axios.get(`${API_URL}/api/monitors/${monitorId}/submonitors`);
        setSubmonitors(subRes.data || []);
      } catch (e) { /* silent */ }
      // Load Audit v3 (prefer over v1)
      try {
        const av3Res = await axios.get(`${API_URL}/api/monitors/${monitorId}/audit-v3`);
        if (av3Res.data && av3Res.data.version === '3.0') {
          setAuditV3(av3Res.data);
        } else {
          // Fallback to old audit
          try {
            const oldAuditRes = await axios.get(`${API_URL}/api/monitors/${monitorId}/geo-audit`);
            if (oldAuditRes.data && oldAuditRes.data.overall_score) setGeoAudit(oldAuditRes.data);
          } catch (e2) { /* silent */ }
        }
      } catch (e) {
        try {
          const oldAuditRes = await axios.get(`${API_URL}/api/monitors/${monitorId}/geo-audit`);
          if (oldAuditRes.data && oldAuditRes.data.overall_score) setGeoAudit(oldAuditRes.data);
        } catch (e2) { /* silent */ }
      }
      // Load hypotheses
      try {
        const hypRes = await axios.get(`${API_URL}/api/hypotheses?geo_monitor_id=${geoId}`);
        setHypotheses(hypRes.data || []);
      } catch (e) { /* silent */ }
      // D5: Load pixel stats (AI Traffic analytics)
      try {
        const pixStatsRes = await axios.get(API_URL + "/api/pixel/stats/" + monitorId + "?period=" + pixelPeriod, { withCredentials: true });
        if (pixStatsRes.data) setPixelStats(pixStatsRes.data);
      } catch (e) { /* silent */ }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [API_URL, geoId, monitorId, timeFilter, pixelPeriod]);

  const loadBillingInfo = async () => {
    try {
      const [balRes, costRes] = await Promise.all([
        axios.get(`${API_URL}/api/billing/balance`),
        axios.get(`${API_URL}/api/billing/operation-costs`),
      ]);
      setCreditBalance(balRes.data.balance);
      setOpCosts(costRes.data);
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    loadData();
    loadBillingInfo();
  }, [loadData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll task progress when analyzing
  useEffect(() => {
    if (geoMonitor?.status !== 'analyzing') return;
    const tid = geoMonitor?.task_id || taskId;

    const poll = async () => {
      if (tid) {
        try {
          const res = await axios.get(`${API_URL}/api/tasks/${tid}`);
          const s = res.data;
          setProgressMessage(s.progress || t('geo.обработка'));
          if (s.status === 'completed' || s.status === 'failed') {
            setTaskId(null);
            loadData();
            loadBillingInfo();
            return;
          }
        } catch {
          loadData();
          return;
        }
      } else {
        // Не перезагружать данные если анализ только что запущен (task_id ещё не получен)
        if (!analysisJustStarted.current) {
          loadData();
        }
      }
    };

    poll();
    const interval = setInterval(poll, 3000);
    return () => clearInterval(interval);
  }, [geoMonitor?.status, geoMonitor?.task_id, taskId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStartAnalysis = async () => {
    analysisJustStarted.current = true;
    setAnalyzing(true);
    setProgressMessage(t('geo.запуск_анализа'));
    setGeoMonitor(prev => prev ? { ...prev, status: 'analyzing' } : prev);
    toast.info(t('geo.гео_анализ_запущен'));
    // Загрузить статус очереди
    try {
      const qRes = await axios.get(`${API_URL}/api/queue-status`);
      setQueueInfo(qRes.data);
    } catch (e) {}
    try {
      const res = await axios.post(`${API_URL}/api/geo/${geoId}/analyze`, {});
      if (res.data.task_id) setTaskId(res.data.task_id);
      analysisJustStarted.current = false;
      loadBillingInfo();
    } catch (err) {
      analysisJustStarted.current = false;
      loadData();
      if (err.response?.status === 402) {
        toast.error(err.response.data.detail || t('geo.недостаточно_кредитов'));
      } else {
        toast.error(t('geo.ошибка_запуска'));
      }
    } finally {
      setAnalyzing(false);
    }
  };

  // --- Hypothesis handlers ---
  const handleAcceptRecommendation = async (rec) => {
    try {
      const payload = {
        monitor_id: monitorId,
        geo_monitor_id: geoId,
        target_type: 'location',
        location: geoMonitor?.geo_label || '',
        source_type: 'monitoring',
        title: rec.title,
        priority: rec.priority || 'medium',
        engines: rec.engines || [],
        category: rec.category || 'content',
        fact: rec.fact || '',
        gap: rec.gap || '',
        hypothesis_text: rec.hypothesis || '',
        tasks: (rec.tasks || []).map(t => ({ id: t.id, text: t.text })),
        verification: rec.verification || {},
      };
      await axios.post(`${API_URL}/api/hypotheses`, payload);
      toast.success(t('geo.гипотеза_добавлена'));
      loadData();
    } catch (err) {
      toast.error(t('geo.ошибка_гипотезы'));
    }
  };

  const handleToggleTask = async (hypId, taskId, completed) => {
    try {
      await axios.patch(`${API_URL}/api/hypotheses/${hypId}/tasks`, { task_id: taskId, completed });
      setHypotheses(prev => prev.map(h => {
        if (h.id !== hypId) return h;
        return { ...h, tasks: h.tasks.map(t => t.id === taskId ? { ...t, completed } : t) };
      }));
    } catch (err) {
      toast.error(t('geo.ошибка_обновления_задачи'));
    }
  };

  const handleCheckHypothesis = async (hypId) => {
    setCheckingHyp(hypId);
    try {
      const res = await axios.post(`${API_URL}/api/hypotheses/${hypId}/check`);
      toast.success(t('geo.вердикт', { verdict: t('geo.' + (VERDICT_STYLES[res.data.verdict]?.labelKey || res.data.verdict)) }));
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('geo.ошибка_проверки'));
    } finally {
      setCheckingHyp(null);
    }
  };

  const handleUpdateHypStatus = async (hypId, status) => {
    try {
      await axios.patch(`${API_URL}/api/hypotheses/${hypId}`, { status });
      toast.success(t('geo.статус_обновлён'));
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('geo.ошибка_обновления'));
    }
  };

  // --- Product handlers ---
  const handleAddProduct = async () => {
    if (!newProduct.name.trim()) return;
    if (!newProduct.url.trim()) {
      toast.error(t('geo.url_товара_обязателен') || 'Product URL is required');
      return;
    }
    setAddingProduct(true);
    try {
      await axios.post(`${API_URL}/api/monitors/${monitorId}/products`, newProduct);
      setNewProduct({ name: '', url: '', description: '', price: '', geo: {}, locale: '' });
      toast.success(t('geo.товар_добавлен'));
      loadData();
    } catch (err) { toast.error(t('geo.ошибка_добавления_товара')); }
    finally { setAddingProduct(false); }
  };

  const handleRemoveProduct = async (productId) => {
    try {
      await axios.delete(`${API_URL}/api/monitors/${monitorId}/products/${productId}`);
      toast.success(t('geo.товар_удалён'));
      loadData();
    } catch (err) { toast.error(t('geo.ошибка_удаления_товара')); }
  };

  const handleProductClick = async (productName) => {
    if (creatingSubmonitor) return;
    setCreatingSubmonitor(productName);
    try {
      const productData = (monitor.products || []).find(p => p.name === productName);
      if (!productData?.url) {
        toast.error(t('geo.url_товара_обязателен') || 'Product URL is required');
        setCreatingSubmonitor(null);
        return;
      }
      const { data } = await axios.post(`${API_URL}/api/monitors/${monitorId}/submonitors`, {
        product_name: productName,
        product_page_url: productData.url,
        product_price: productData?.price || '',
        geo: productData?.geo || {},
        locale: productData?.locale || 'en',
      });
      navigate(`/monitor/${monitorId}/product/${data.id}`);
    } catch (err) { toast.error(t('geo.ошибка_создания_суб')); }
    finally { setCreatingSubmonitor(null); }
  };

  // --- Custom queries handlers ---
  const handleAddQuery = async () => {
    const q = newQuery.trim();
    if (!q) return;
    const updated = [...customQueries, q];
    setCustomQueries(updated);
    setNewQuery('');
    try {
      await axios.put(`${API_URL}/api/monitors/${monitorId}/custom-queries`, { queries: updated });
    } catch (err) { toast.error(t('geo.ошибка_сохранения_запроса')); }
  };

  const handleRemoveQuery = async (idx) => {
    const updated = customQueries.filter((_, i) => i !== idx);
    setCustomQueries(updated);
    try {
      await axios.put(`${API_URL}/api/monitors/${monitorId}/custom-queries`, { queries: updated });
    } catch (err) { toast.error(t('geo.ошибка_удаления_запроса')); }
  };

  // SESSION 83: Brand Resources handlers
  const handleAddBrandResource = async () => {
    const url = newBrandResource.trim();
    if (!url) return;
    const updated = [...brandResources, url];
    setBrandResources(updated);
    setNewBrandResource('');
    try {
      await axios.patch(`${API_URL}/api/geo/${geoId}/brand-resources`, { brand_resources: updated });
    } catch (err) { toast.error('Failed to save brand resource'); }
  };
  const handleRemoveBrandResource = async (idx) => {
    const updated = brandResources.filter((_, i) => i !== idx);
    setBrandResources(updated);
    try {
      await axios.patch(`${API_URL}/api/geo/${geoId}/brand-resources`, { brand_resources: updated });
    } catch (err) { toast.error('Failed to remove brand resource'); }
  };

  // ─── Audit v3 handler ───
  const runAuditV3 = async () => {
    setAuditV3Loading(true);
    try {
      const res = await axios.post(`${API_URL}/api/monitors/${monitorId}/audit-v3`);
      if (res.data && res.data.version === '3.0') {
        setAuditV3(res.data);
        toast.success('Site audit completed');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Audit failed');
    } finally { setAuditV3Loading(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <RefreshCw className="w-6 h-6 text-[#1BB594] animate-spin" />
      </div>
    );
  }

  if (!geoMonitor) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-slate-500">{t('geo.не_найдена')}</p>
      </div>
    );
  }

  const engines = results?.ai_engine_results || {};
  const competitors = results?.competitor_analysis || {};
  const recommendations = results?.recommendations || [];
  const keyChanges = results?.key_changes || [];
  const monitoringMeta = results?.monitoring_meta || {};
  const sovCi = results?.ci_intervals || {};

  // ── Recompute aggregate metrics from per-engine data ──
  // AI server may average over wrong engine count; frontend recalculates from actual data
  const _engArr = Object.entries(engines);
  const realEngineCount = _engArr.length;
  const _rc = realEngineCount > 0 ? (() => {
    const avg = (fn) => +(_engArr.reduce((s, [, d]) => s + (fn(d) || 0), 0) / realEngineCount).toFixed(2);
    const vis = avg(d => d.visibility_percent);
    const cit = avg(d => d.citation_percent);
    return { mkt: avg(d => d.market_visibility), brd: avg(d => d.brand_visibility), vis, cit, overall: +(0.6 * vis + 0.4 * cit).toFixed(2) };
  })() : null;

  const chartData = history.map(h => {
    const dataPoint = {
      date: new Date(h.created_at).toLocaleDateString(dateLocale, { day: '2-digit', month: '2-digit' }),
      sov: h.sov_overall,
      market_sov: h.market_sov,
      brand_sov: h.brand_sov,
    };
    Object.entries(h.ai_engine_results || {}).forEach(([engine, data]) => {
      if (chartMetric === 'visibility') {
        dataPoint[engine] = data.visibility_percent;
      } else {
        dataPoint[`${engine}_citation`] = data.citation_percent;
      }
    });
    return dataPoint;
  });

  // Split hypotheses by status
  const activeHyps = hypotheses.filter(h => ['active', 'checking', 'too_early'].includes(h.status));
  const completedHyps = hypotheses.filter(h => ['confirmed', 'refuted', 'neutral', 'abandoned', 'declined', 'closed'].includes(h.status));

  // ─── Audit v3 computed groups ───
  const auditChecks = auditV3?.checks || [];
  const auditFixNow = auditChecks.filter(c => c.priority === 'critical' && (c.status === 'fail' || c.status === 'partial'));
  const auditShouldFix = auditChecks.filter(c => c.priority === 'high' && (c.status === 'fail' || c.status === 'partial'));
  const auditNiceToHave = auditChecks.filter(c => c.priority === 'medium' && (c.status === 'fail' || c.status === 'partial'));
  const auditPassed = auditChecks.filter(c => c.status === 'pass');

  return (
    <div className="min-h-screen bg-[#FFFFFF]">
      <header className="bg-white/80 backdrop-blur-xl border-b border-[#E2DDD8] sticky top-0 z-50">
        <div className="w-full max-w-[1400px] mx-auto px-6 md:px-12 py-4 flex items-center gap-4">
          <button onClick={() => navigate(`/monitor/${monitorId}`)} className="flex items-center gap-2 text-[#1BB594] hover:underline font-semibold">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-baseline gap-1.5">
            <img src="/arpai_logo.png" alt="ARPAI" style={{height: '1.25rem'}} />
            <span className="text-xl font-bold text-[#2F2E2F] tracking-[0.15em]">GEO</span>
            <span className="hidden sm:inline text-[10px] text-slate-400 ml-2">{t('geo.сервис_слоган')}</span>
          </div>
        </div>
      </header>

      <main id="geo-pdf-content" className="w-full max-w-[1400px] mx-auto px-6 md:px-12 py-16 md:py-24">
        {/* Title */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <MapPin className="w-6 h-6 text-[#1BB594]" />
            <h1 className="text-4xl sm:text-5xl tracking-tighter leading-none font-black text-[#2F2E2F]" data-testid="geo-title">
              {geoMonitor.geo_label}
            </h1>
          </div>
          <p className="text-base text-slate-600">{geoMonitor.geo_string} | {geoMonitor.parent_company_info?.company_name || ''}</p>
        </div>

        {/* Buttons */}
        <div className="mb-8 flex items-center gap-3">
          {results && <PdfDownloadButton targetId="geo-pdf-content" filename={`geo-${geoMonitor.geo_label}`} />}
          {geoMonitor.status === 'completed' && (
            <div className="flex items-center gap-3">
              <button onClick={handleStartAnalysis} disabled={analyzing} className="rounded-none font-bold tracking-widest uppercase text-xs px-8 py-3 bg-[#1BB594] hover:bg-[#159A7F] text-white transition-transform active:translate-y-1 flex items-center gap-2 disabled:opacity-50">
                <RefreshCw className={`w-4 h-4 ${analyzing ? 'animate-spin' : ''}`} />
                {analyzing ? t('geo.запуск') : t('geo.мониторинг_локации')}
              </button>
              {opCosts.sub_monitor > 0 && (
                <span className="text-xs text-slate-500 flex items-center gap-1">
                  <Coins className="w-3 h-3" /> ${(opCosts.sub_monitor / 100).toFixed(2)} (balance: {creditBalance != null ? '$' + (creditBalance / 100).toFixed(2) : '...'})
                </span>
              )}
            </div>
          )}
        </div>

        {/* Schedule */}
        {geoMonitor.status === 'completed' && monitor && (
          <div className="mb-8"><ScheduleConfig targetId={geoId} targetType="location" /></div>
        )}

        {/* Pending state */}
        {geoMonitor.status === 'pending' && (
          <div className="mb-12 p-8 bg-blue-50 border border-blue-200">
            <h3 className="text-xl font-bold text-blue-900 mb-4">{t('geo.запустить_анализ_вопрос')}</h3>
            <p className="text-blue-800 mb-6">
              {t('geo.запустить_анализ_описание', { location: geoMonitor.geo_label })}
            </p>
            <button onClick={handleStartAnalysis} disabled={analyzing} className="rounded-none font-bold tracking-widest uppercase text-xs px-8 py-4 bg-[#1BB594] hover:bg-[#159A7F] text-white flex items-center gap-2 disabled:opacity-50">
              <Play className="w-5 h-5" /> {analyzing ? t('geo.запуск') : t('geo.запустить_гео_анализ')}
            </button>
            {opCosts.sub_monitor > 0 && <span className="text-xs text-slate-500 mt-2 flex items-center gap-1"><Coins className="w-3 h-3" /> ${(opCosts.sub_monitor / 100).toFixed(2)}</span>}
          </div>
        )}

        {/* Analyzing state */}
        {geoMonitor.status === 'analyzing' && (
          <div className="mb-12 p-8 bg-blue-50 border border-blue-200">
            <div className="flex items-start gap-4">
              <RefreshCw className="w-6 h-6 text-blue-600 animate-spin flex-shrink-0 mt-1" />
              <div className="flex-1">
                <h3 className="text-xl font-bold text-blue-900 mb-2">{t('geo.анализ_локации', { location: geoMonitor.geo_label })}</h3>
                <p className="text-sm text-blue-700 mb-4">{progressMessage || geoMonitor.progress_message || t('geo.подготовка')}</p>
                {queueInfo && queueInfo.waiting > 0 && (
                  <p className="text-xs text-amber-600 mb-2 flex items-center gap-1">{t('geo.в_очереди', { waiting: queueInfo.waiting, active: queueInfo.active, max: queueInfo.max_concurrent })}</p>
                )}
                {queueInfo && queueInfo.active > 0 && queueInfo.waiting === 0 && (
                  <p className="text-xs text-blue-500 mb-2">{t('geo.активных_мониторингов', { active: queueInfo.active, max: queueInfo.max_concurrent })}</p>
                )}
                <div className="w-full bg-blue-200 h-2 overflow-hidden">
                  <div className="h-full bg-blue-600 animate-progress-bar" style={{width: '100%', animation: 'progress-indeterminate 2s ease-in-out infinite'}} />
                </div>
                <p className="text-xs text-blue-500 mt-2">{t('geo.можно_закрыть')}</p>
              </div>
            </div>
          </div>
        )}

        {/* Error state */}
        {geoMonitor.status === 'error' && (
          <div className="mb-12 p-8 bg-red-50 border border-red-300">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-8 h-8 text-red-500 flex-shrink-0 mt-1" />
              <div className="flex-1">
                <h3 className="text-xl font-bold text-red-900 mb-2">{t('geo.ошибка_анализа')}</h3>
                <p className="text-red-700 text-sm mb-4">{geoMonitor.error_message || t('geo.попробуйте_снова')}</p>
                <button onClick={handleStartAnalysis} disabled={analyzing} className="rounded-none font-bold tracking-widest uppercase text-xs px-6 py-3 bg-red-600 hover:bg-red-700 text-white flex items-center gap-2 disabled:opacity-50">
                  <RefreshCw className={`w-4 h-4 ${analyzing ? 'animate-spin' : ''}`} /> {t('geo.повторить')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ═══ RESULTS ═══ */}
        {results && (
          <>
            {/* ─── Key Changes Banner ─── */}
            {keyChanges.length > 0 && (
              <div className="mb-8 flex flex-wrap gap-3" data-testid="key-changes">
                {keyChanges.map((change, idx) => (
                  <div key={idx} className={`inline-flex items-center gap-2 px-4 py-2 border text-sm font-medium ${
                    change.direction === 'up' ? 'bg-green-50 border-green-300 text-green-800' :
                    change.direction === 'down' ? 'bg-red-50 border-red-300 text-red-800' :
                    'bg-slate-50 border-slate-300 text-slate-700'
                  }`}>
                    {change.direction === 'up' ? <ArrowUpRight className="w-4 h-4" /> :
                     change.direction === 'down' ? <ArrowDownRight className="w-4 h-4" /> :
                     <Minus className="w-4 h-4" />}
                    {change.text}
                  </div>
                ))}
              </div>
            )}

            {/* ─── SOV Scorecard with CI ─── */}
            <div className="mb-12 bg-white border border-[#E2DDD8] p-6 sm:p-8" data-testid="sov-scorecard">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="text-center md:border-r border-[#E2DDD8]">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">MARKET SOV</p>
                  <p className="text-xs text-slate-400 mb-2">{t('geo.конкурентная_видимость')}</p>
                  <div className="text-5xl sm:text-6xl lg:text-7xl tracking-tighter font-black text-[#1BB594]">
                    {_rc?.mkt ?? results.market_sov ?? '—'}%
                  </div>
                  <CiBadge ci={sovCi.market_sov} className="mt-1" />
                  <p className="text-xs text-slate-500 mt-2">{t('geo.запросы_без_бренда')}</p>
                </div>
                <div className="text-center md:border-r border-[#E2DDD8]">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">BRAND SOV</p>
                  <p className="text-xs text-slate-400 mb-2">{t('geo.узнаваемость_бренда')}</p>
                  <div className="text-5xl sm:text-6xl lg:text-7xl tracking-tighter font-black text-emerald-600">
                    {_rc?.brd ?? results.brand_sov ?? '—'}%
                  </div>
                  <p className="text-xs text-slate-500 mt-2">{t('geo.запросы_с_брендом')}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">{t('geo.общий_sov')}</p>
                  <p className="text-xs text-slate-400 mb-2">Share of Voice</p>
                  <div className="text-5xl sm:text-6xl lg:text-7xl tracking-tighter font-black text-slate-400">
                    {_rc?.overall ?? results.sov_overall}%
                  </div>
                  <CiBadge ci={sovCi.sov_overall} className="mt-1" />
                  <p className="text-xs text-slate-500 mt-2">{results.sov_formula || '0.6×Vis + 0.4×Cit'}</p>
                </div>
              </div>
              {/* Sub-metrics row */}
              <div className="mt-6 pt-4 border-t border-[#E2DDD8] flex flex-wrap gap-6 justify-center text-center">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">{t('geo.видимость')}</p>
                  <p className="text-lg font-bold text-slate-700">{_rc?.vis ?? results.visibility_global ?? '—'}%</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">{t('geo.цитируемость')}</p>
                  <p className="text-lg font-bold text-slate-700">{_rc?.cit ?? results.citation_global ?? '—'}%</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">{t('geo.движков')}</p>
                  <p className="text-lg font-bold text-slate-700">{realEngineCount}/{realEngineCount}</p>
                </div>
                {monitoringMeta.duration_seconds && (
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-slate-500">{t('geo.время')}</p>
                    <p className="text-lg font-bold text-slate-700">{Math.round(monitoringMeta.duration_seconds)}{t('geo.сек')}</p>
                  </div>
                )}
              </div>
            </div>

            {/* ─── AI Engine Results ─── */}
            <div className="mb-12">
              <h2 className="text-2xl sm:text-3xl tracking-tight font-bold text-[#2F2E2F] mb-6">{t('geo.результаты_ai')}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0" data-testid="ai-engines-grid">
                {Object.entries(engines).flatMap(([engine, data], idx) => {
                  const cards = [];
                  // Insert ARPAI logo placeholder at position 5 (after 4th engine)
                  if (idx === 4) {
                    cards.push(
                      <div key="arpai-placeholder" className="bg-white border border-[#E2DDD8] flex items-center justify-center" style={{ padding: '20px' }}>
                        <img src="/Logo_A.svg" alt="ARPAI GEO" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                      </div>
                    );
                  }
                  cards.push(
                  <div key={engine} className="bg-white border border-[#E2DDD8] p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-3 h-3" style={{ backgroundColor: AI_ENGINE_COLORS[engine] }} />
                      <h3 className="text-lg font-bold text-slate-900">{engine}</h3>
                      {!data.active && <span className="text-[10px] bg-red-100 text-red-600 px-1.5 py-0.5">{t('geo.неактивен')}</span>}
                      {data.web_search_enabled === false && <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5">{t('geo.без_поиска')}</span>}
                    </div>
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-slate-500">Market</p>
                          <p className="text-2xl font-black text-[#1BB594]">{data.market_visibility ?? data.visibility_percent}%</p>
                          <p className="text-[10px] text-slate-400">{data.market_mentions ?? '?'}/{data.market_total ?? '?'}</p>
                          <CiBadge ci={data.ci?.market_visibility} />
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-slate-500">Brand</p>
                          <p className="text-2xl font-black text-emerald-600">{data.brand_visibility ?? data.visibility_percent}%</p>
                          <p className="text-[10px] text-slate-400">{data.brand_q_mentions ?? '?'}/{data.brand_q_total ?? '?'}</p>
                          <CiBadge ci={data.ci?.brand_visibility} />
                        </div>
                      </div>
                      <div className="pt-2 border-t border-slate-200">
                        <div className="flex justify-between items-baseline">
                          <div>
                            <p className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1 mb-1"><TrendingUp className="w-3 h-3" /> {t('geo.видимость')}</p>
                            <p className="text-xl font-black text-[#2F2E2F]">{data.visibility_percent}%</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1 mb-1 justify-end"><LinkIcon className="w-3 h-3" /> {t('geo.цитируемость')}</p>
                            <p className="text-xl font-black text-[#2F2E2F]">{data.citation_percent ?? 0}%</p>
                          </div>
                        </div>
                        <CiBadge ci={data.ci?.visibility} />
                        {data.citation_note && <p className="text-[10px] text-orange-500 mt-1">{data.citation_note}</p>}
                      </div>
                      <SentimentBar breakdown={data.sentiment_breakdown} />
                      <PositionBar breakdown={data.position_breakdown} />
                    </div>
                  </div>
                  );
                  return cards;
                })}
              </div>
            </div>

            {/* ─── Hypotheses / Plan ─── */}
            {(activeHyps.length > 0 || completedHyps.length > 0) && (
              <div className="mb-12 bg-white border border-[#E2DDD8] p-6 sm:p-8" data-testid="hypotheses-section">
                <h2 className="text-2xl sm:text-3xl tracking-tight font-bold text-[#2F2E2F] mb-6 flex items-center gap-2">
                  <Beaker className="w-6 h-6 text-[#1BB594]" /> {t('geo.план_работ')}
                </h2>

                {/* Active hypotheses */}
                {activeHyps.length > 0 && (
                  <div className="space-y-3 mb-6">
                    {activeHyps.map(hyp => {
                      const isExpanded = expandedHyp === hyp.id;
                      const completedTasks = (hyp.tasks || []).filter(t => t.completed).length;
                      const totalTasks = (hyp.tasks || []).length;
                      const vstyle = VERDICT_STYLES[hyp.status] || VERDICT_STYLES.active;
                      return (
                        <div key={hyp.id} className="border border-slate-200 bg-slate-50">
                          <div className="p-4 flex items-start gap-3 cursor-pointer" onClick={() => setExpandedHyp(isExpanded ? null : hyp.id)}>
                            <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase ${vstyle.bg} flex-shrink-0 mt-0.5`}>{t('geo.' + vstyle.labelKey)}</span>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-slate-900">{hyp.title}</p>
                              <div className="flex items-center gap-3 mt-1">
                                {hyp.engines?.map(e => <span key={e} className="text-[10px] font-bold px-1.5 py-0.5" style={{ backgroundColor: `${AI_ENGINE_COLORS[e]}20`, color: AI_ENGINE_COLORS[e] }}>{e}</span>)}
                                <span className="text-[10px] text-slate-400">{completedTasks}/{totalTasks} {t('geo.задач')}</span>
                                {hyp.check_date && <span className="text-[10px] text-slate-400 flex items-center gap-0.5"><Clock className="w-3 h-3" /> {new Date(hyp.check_date).toLocaleDateString(dateLocale)}</span>}
                              </div>
                            </div>
                            {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                          </div>
                          {isExpanded && (
                            <div className="px-4 pb-4 border-t border-slate-200 pt-3">
                              {hyp.fact && <p className="text-xs text-slate-600 mb-2"><span className="font-bold">{t('geo.факт')}</span> {hyp.fact}</p>}
                              {hyp.gap && <p className="text-xs text-slate-600 mb-2"><span className="font-bold">{t('geo.разрыв')}</span> {hyp.gap}</p>}
                              {hyp.hypothesis_text && <p className="text-xs text-slate-700 mb-3 italic">"{hyp.hypothesis_text}"</p>}
                              {/* Tasks */}
                              {hyp.tasks?.length > 0 && (
                                <div className="space-y-1.5 mb-3">
                                  {hyp.tasks.map(task => (
                                    <label key={task.id} className="flex items-start gap-2 cursor-pointer group">
                                      <button onClick={(e) => { e.stopPropagation(); handleToggleTask(hyp.id, task.id, !task.completed); }} className="mt-0.5 flex-shrink-0">
                                        {task.completed ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Circle className="w-4 h-4 text-slate-300 group-hover:text-[#1BB594]" />}
                                      </button>
                                      <span className={`text-sm ${task.completed ? 'line-through text-slate-400' : 'text-slate-700'}`}>{task.text}</span>
                                    </label>
                                  ))}
                                </div>
                              )}
                              {/* Verification info */}
                              {hyp.verification?.expected_signal && (
                                <p className="text-[10px] text-slate-400 mb-3">
                                  <Target className="w-3 h-3 inline mr-1" />
                                  {t('geo.ожидаемый_сигнал', { signal: hyp.verification.expected_signal })}
                                </p>
                              )}
                              {/* Actions */}
                              <div className="flex gap-2 flex-wrap">
                                <button onClick={() => handleCheckHypothesis(hyp.id)} disabled={checkingHyp === hyp.id} className="text-xs px-3 py-1.5 bg-[#1BB594] text-white hover:bg-[#159A7F] disabled:opacity-50 flex items-center gap-1">
                                  {checkingHyp === hyp.id ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Beaker className="w-3 h-3" />}
                                  {t('geo.проверить')}
                                </button>
                                <button onClick={() => handleUpdateHypStatus(hyp.id, 'abandoned')} className="text-xs px-3 py-1.5 border border-slate-300 text-slate-600 hover:bg-slate-100">
                                  {t('geo.отклонить')}
                                </button>
                              </div>
                              {/* Last verification result */}
                              {hyp.verification_results?.length > 0 && (
                                <div className="mt-3 p-3 bg-white border border-slate-200">
                                  <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{t('geo.последняя_проверка')}</p>
                                  <p className="text-xs text-slate-600">{hyp.verification_results[hyp.verification_results.length - 1].comment}</p>
                                  <p className="text-[10px] text-slate-400 mt-1">
                                    {new Date(hyp.verification_results[hyp.verification_results.length - 1].date).toLocaleDateString(dateLocale)} |
                                    delta: {hyp.verification_results[hyp.verification_results.length - 1].delta_pp}{t('geo.пп')} |
                                    p={hyp.verification_results[hyp.verification_results.length - 1].p_value}
                                  </p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Completed hypotheses (collapsed) */}
                {completedHyps.length > 0 && (
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400 mb-2">{t('geo.завершённые', { count: completedHyps.length })}</p>
                    <div className="space-y-1">
                      {completedHyps.map(hyp => {
                        const vstyle = VERDICT_STYLES[hyp.status] || VERDICT_STYLES.closed;
                        return (
                          <div key={hyp.id} className="flex items-center gap-2 p-2 bg-slate-50 border border-slate-100">
                            <span className={`inline-block px-1.5 py-0.5 text-[9px] font-bold uppercase ${vstyle.bg}`}>{t('geo.' + vstyle.labelKey)}</span>
                            <p className="text-xs text-slate-500 flex-1 truncate">{hyp.title}</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ─── Recommendations v2 (Fact → Action cards) ─── */}
            {(() => {
              // SESSION 91: Recommendations v2 — flat cards grouped by category
              // Support both old format (hypothesis) and new (fact→action)
              const isV2 = recommendations.length > 0 && recommendations[0].action;
              const CATEGORY_ORDER = ['technical', 'content', 'opportunities'];
              const CATEGORY_LABELS = { technical: t('geo.rec_technical', 'Technical'), content: t('geo.rec_content', 'Content & visibility'), opportunities: t('geo.rec_opportunities', 'Opportunities') };
              const CATEGORY_ICONS = { technical: '⚙️', content: '📝', opportunities: '💡' };
              const EFFORT_STYLES = {
                '2-3 hours': 'bg-emerald-50 text-emerald-700', '1-2 days': 'bg-emerald-50 text-emerald-700',
                '1 week': 'bg-amber-50 text-amber-700', '1-2 weeks': 'bg-amber-50 text-amber-700',
                '2-4 weeks': 'bg-purple-50 text-purple-700', '1-2 months': 'bg-purple-50 text-purple-700',
              };
              const PRIORITY_BORDER = { high: 'border-l-red-400', medium: 'border-l-amber-400', low: 'border-l-blue-400' };

              // Separate active recommendations from known issues (recurring 4+ cycles ~56+ days)
              const knownIssueThreshold = 56 * 24 * 60 * 60 * 1000; // 56 days in ms
              const now = Date.now();
              let activeRecs = [];
              let knownIssues = [];
              recommendations.forEach(rec => {
                if (rec.first_seen) {
                  const age = now - new Date(rec.first_seen).getTime();
                  if (age > knownIssueThreshold) { knownIssues.push(rec); return; }
                }
                activeRecs.push(rec);
              });

              // Group by category
              const grouped = {};
              activeRecs.forEach(rec => {
                const cat = rec.category || 'content';
                if (!grouped[cat]) grouped[cat] = [];
                grouped[cat].push(rec);
              });

              // Calculate days since first_seen
              const daysSince = (dateStr) => {
                if (!dateStr) return 0;
                return Math.floor((now - new Date(dateStr).getTime()) / (24 * 60 * 60 * 1000));
              };

              if (recommendations.length === 0) return null;

              // Fallback for old format — keep old accordion rendering
              if (!isV2) {
                return (
                  <div className="mb-12 bg-white border border-[#E2DDD8] p-6 sm:p-8" data-testid="recommendations-section">
                    <h2 className="text-2xl sm:text-3xl tracking-tight font-bold text-[#2F2E2F] mb-6">{t('geo.рекомендации')}</h2>
                    <div className="space-y-3">
                      {recommendations.map((rec, idx) => {
                        const isExpanded = expandedRec === idx;
                        const pstyle = PRIORITY_STYLES[rec.priority] || PRIORITY_STYLES.medium;
                        return (
                          <div key={idx} className="border border-slate-200 bg-slate-50">
                            <div className="p-4 flex items-start gap-3 cursor-pointer" onClick={() => setExpandedRec(isExpanded ? null : idx)}>
                              <span className={`inline-block px-2 py-1 text-xs font-bold uppercase tracking-wider text-white flex-shrink-0 ${pstyle.bg}`}>{t('geo.' + pstyle.labelKey)}</span>
                              <p className="text-sm font-semibold text-slate-900 flex-1">{rec.title}</p>
                              {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                            </div>
                            {isExpanded && (
                              <div className="px-4 pb-4 border-t border-slate-200 pt-3">
                                {rec.fact && <p className="text-xs text-slate-600 mb-2"><span className="font-bold">{t('geo.факт')}</span> {rec.fact}</p>}
                                {rec.gap && <p className="text-xs text-slate-600 mb-2"><span className="font-bold">{t('geo.разрыв')}</span> {rec.gap}</p>}
                                {rec.hypothesis && <p className="text-xs text-slate-700 mb-3 italic">"{rec.hypothesis}"</p>}
                                {(rec.tasks || []).map(tk => <p key={tk.id} className="text-xs text-slate-600 ml-2">• {tk.text}</p>)}
                                {rec.verification && <p className="text-[10px] text-slate-400 mt-2"><Target className="w-3 h-3 inline mr-1" />{rec.verification.expected_signal}</p>}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              }

              // ─── V2 Rendering: Flat cards, grouped by category ───
              return (
                <div className="mb-12 bg-white border border-[#E2DDD8] p-6 sm:p-8" data-testid="recommendations-section">
                  <h2 className="text-2xl sm:text-3xl tracking-tight font-bold text-[#2F2E2F] mb-6">{t('geo.рекомендации')}</h2>

                  {activeRecs.length === 0 && knownIssues.length === 0 && (
                    <div className="text-center py-8 text-slate-400">
                      <CheckCircle className="w-8 h-8 mx-auto mb-2 text-[#1BB594]" />
                      <p className="text-sm">{t('geo.rec_all_healthy', 'All metrics look healthy. No action needed.')}</p>
                    </div>
                  )}

                  {CATEGORY_ORDER.map(cat => {
                    const catRecs = grouped[cat];
                    if (!catRecs || catRecs.length === 0) return null;
                    return (
                      <div key={cat} className="mb-6">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-sm">{CATEGORY_ICONS[cat]}</span>
                          <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">{CATEGORY_LABELS[cat]}</span>
                          <div className="flex-1 h-px bg-slate-200" />
                        </div>
                        <div className="space-y-3">
                          {catRecs.map((rec, idx) => {
                            const days = daysSince(rec.first_seen);
                            const effortStyle = EFFORT_STYLES[rec.effort] || 'bg-slate-100 text-slate-600';
                            const borderColor = PRIORITY_BORDER[rec.priority] || 'border-l-slate-300';
                            return (
                              <div key={`${cat}-${idx}`} className={`bg-white border border-slate-200 border-l-[3px] ${borderColor} p-4`}>
                                <div className="flex items-center gap-2 mb-2 flex-wrap">
                                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 ${rec.priority === 'high' ? 'bg-red-50 text-red-700' : rec.priority === 'low' ? 'bg-blue-50 text-blue-700' : 'bg-amber-50 text-amber-700'}`}>
                                    {t('geo.' + (PRIORITY_STYLES[rec.priority]?.labelKey || 'средний'))}
                                  </span>
                                  {rec.effort && (
                                    <span className={`text-[10px] font-medium px-2 py-0.5 ${effortStyle}`}>
                                      ⏱ {rec.effort}
                                    </span>
                                  )}
                                  {days > 14 && (
                                    <span className="text-[10px] px-2 py-0.5 bg-orange-50 text-orange-600">
                                      {t('geo.rec_unresolved', { days })}
                                    </span>
                                  )}
                                  <span className="text-[10px] text-slate-400 ml-auto">
                                    {(rec.engines || []).join(', ')}
                                  </span>
                                </div>
                                <p className="text-sm font-semibold text-slate-900 mb-2">{rec.title}</p>
                                <div className="bg-slate-50 border border-slate-100 px-3 py-2 mb-2">
                                  <p className="text-xs text-slate-600">
                                    <span className="text-slate-400 mr-1">📊</span>
                                    {rec.fact}
                                  </p>
                                </div>
                                <p className="text-xs text-slate-800 mb-1.5">
                                  <span className="text-[#1BB594] mr-1">→</span>
                                  {rec.action}
                                </p>
                                <p className="text-[11px] text-slate-400">
                                  <Target className="w-3 h-3 inline mr-1" />
                                  {rec.expected_result}
                                </p>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}

                  {/* Known Issues — collapsed section for old recurring recommendations */}
                  {knownIssues.length > 0 && (
                    <div className="mt-6 border-t border-slate-200 pt-4">
                      <button
                        onClick={() => setExpandedRec(expandedRec === 'known' ? null : 'known')}
                        className="flex items-center gap-2 text-xs text-slate-400 hover:text-slate-600"
                      >
                        {expandedRec === 'known' ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                        {t('geo.rec_known_issues', { count: knownIssues.length })}
                      </button>
                      {expandedRec === 'known' && (
                        <div className="mt-3 space-y-2">
                          {knownIssues.map((rec, idx) => (
                            <div key={`ki-${idx}`} className="bg-slate-50 border border-slate-200 px-3 py-2">
                              <p className="text-xs text-slate-500">
                                <span className="font-medium text-slate-700">{rec.title}</span>
                                {' — '}{rec.fact}
                                <span className="ml-2 text-orange-500">({daysSince(rec.first_seen)}d)</span>
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* ─── Custom Queries ─── */}
            {monitor && (
              <div className="mb-12 bg-white border border-[#E2DDD8] p-6 sm:p-8" data-testid="custom-queries-section">
                <h2 className="text-2xl sm:text-3xl tracking-tight font-bold text-[#2F2E2F] mb-4">{t('geo.свои_запросы')}</h2>
                <p className="text-xs text-slate-400 mb-3">{t('geo.свои_запросы_описание')}</p>
                <div className="flex flex-wrap gap-2 mb-3">
                  {customQueries.map((q, idx) => (
                    <span key={idx} className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-700 text-xs border border-blue-200">
                      {q}
                      <button onClick={() => handleRemoveQuery(idx)} className="text-blue-400 hover:text-red-500 ml-1"><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <input type="text" value={newQuery} onChange={(e) => setNewQuery(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleAddQuery()} placeholder={t('geo.ph_запрос')} className="flex-1 px-3 py-2 border border-[#E2DDD8] text-sm focus:outline-none focus:border-[#1BB594]" />
                  <button onClick={handleAddQuery} disabled={!newQuery.trim()} className="rounded-none font-bold tracking-widest uppercase text-xs px-5 py-2 bg-[#1BB594] text-white hover:bg-[#159A7F] disabled:opacity-50 flex items-center gap-1.5 whitespace-nowrap">
                    <PlusCircle className="w-3.5 h-3.5" /> {t('geo.добавить')}
                  </button>
                </div>
              </div>
            )}

            {/* ─── Generated Queries ─── */}
            {results?.query_engine_map && Object.keys(results.query_engine_map).length > 0 && (
              <div className="mb-12 bg-white border border-[#E2DDD8] p-6 sm:p-8" data-testid="queries-section">
                <h2 className="text-2xl sm:text-3xl tracking-tight font-bold text-[#2F2E2F] mb-6">{t('geo.запросы_и_упоминания')}</h2>
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {Object.entries(results.query_engine_map).map(([query, queryEngines], idx) => {
                    const qRankings = rankingsMap[query] || {};
                    const targetPositions = Object.values(qRankings).map(r => r.target_position).filter(p => p != null);
                    const bestPos = targetPositions.length > 0 ? Math.min(...targetPositions) : null;
                    const hasRankData = Object.keys(qRankings).length > 0;
                    const hasBrands = Object.values(qRankings).some(r => r.total_brands > 0);
                    const borderColor = bestPos != null ? (bestPos <= 2 ? '#10B981' : bestPos <= 5 ? '#F59E0B' : '#EF4444') : (hasRankData ? (hasBrands ? '#F59E0B' : '#6B7280') : '#94A3B8');
                    const bgColor = bestPos != null ? (bestPos <= 2 ? '#ECFDF5' : bestPos <= 5 ? '#FFFBEB' : '#FEF2F2') : (hasRankData ? (hasBrands ? '#FFFBEB' : '#F3F4F6') : '#F8FAFC');
                    return (
                      <div key={idx} className="p-2 cursor-pointer hover:shadow-md transition-shadow"
                           style={{ backgroundColor: bgColor, borderLeft: `3px solid ${borderColor}` }}
                           onClick={() => setSelectedQueryRanking({ query, engines: Object.keys(qRankings).length > 0 ? Object.keys(qRankings) : queryEngines, rankings: qRankings })}>
                        <div className="flex items-center justify-between">
                          <p className="text-sm text-slate-900 flex-1">{query}</p>
                          {bestPos ? <span className="text-xs font-bold ml-2 px-2 py-0.5 rounded text-white" style={{ backgroundColor: borderColor }}>#{bestPos}</span> : hasRankData ? (hasBrands ? <span className="text-xs font-bold ml-2 px-2 py-0.5 rounded text-white" style={{ backgroundColor: '#F59E0B' }}>{t('geo.нет_в_топ')}</span> : <span className="text-xs font-bold ml-2 px-2 py-0.5 rounded text-white" style={{ backgroundColor: '#6B7280' }}>{t('geo.ложный')}</span>) : null}
                        </div>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {queryEngines.map(eng => {
                            const r = qRankings[eng];
                            const pos = r?.target_position;
                            const posColor = pos != null ? (pos <= 2 ? '#10B981' : pos <= 5 ? '#F59E0B' : '#EF4444') : null;
                            return (
                              <span key={eng} className="text-[10px] font-bold px-1.5 py-0.5 inline-flex items-center gap-1" style={{ backgroundColor: `${AI_ENGINE_COLORS[eng] || '#94A3B8'}20`, color: AI_ENGINE_COLORS[eng] || '#94A3B8' }}>
                                {eng}
                                {pos != null && <span className="rounded px-1 text-white" style={{ backgroundColor: posColor }}>#{pos}</span>}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}


            {/* ─── Brand Ranking Modal (Session 31) ─── */}
            {selectedQueryRanking && (
              <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedQueryRanking(null)}>
                <div className="bg-white max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6 shadow-xl border border-[#E2DDD8]" onClick={e => e.stopPropagation()}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-[#2F2E2F]">{t('geo.рейтинг_брендов')}</h3>
                    <button onClick={() => setSelectedQueryRanking(null)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
                  </div>
                  <p className="text-sm text-slate-700 mb-4 p-3 bg-slate-50 border-l-2 border-[#1BB594]">{selectedQueryRanking.query}</p>
                  <div className="space-y-3">
                    {selectedQueryRanking.engines.map(eng => {
                      const r = selectedQueryRanking.rankings[eng];
                      if (!r) return (
                        <div key={eng} className="p-3 bg-slate-50 border border-slate-200">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-bold" style={{ color: AI_ENGINE_COLORS[eng] || '#94A3B8' }}>{eng}</span>
                            <span className="text-xs text-slate-400 italic">{t('geo.нет_данных_рейтинг')}</span>
                          </div>
                        </div>
                      );
                      const posColor = r.target_position ? (r.target_position <= 2 ? '#10B981' : r.target_position <= 5 ? '#F59E0B' : '#EF4444') : (r.total_brands > 0 ? '#F59E0B' : '#6B7280');
                      return (
                        <div key={eng} className="p-3 bg-white border border-slate-200">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-bold" style={{ color: AI_ENGINE_COLORS[eng] || '#94A3B8' }}>{eng}</span>
                            <span className="text-sm font-bold" style={{ color: posColor }}>
                              {r.target_position ? t('geo.позиция_из', { pos: r.target_position, total: r.total_brands }) : (r.total_brands > 0 ? t('geo.нет_в_топ_n', { n: r.total_brands }) : t('geo.ложный_запрос'))}
                            </span>
                          </div>
                          {r.brands && r.brands.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {r.brands.map((b, i) => (
                                <span key={i} className={`text-[11px] px-2 py-0.5 border ${b.is_target ? 'bg-emerald-50 border-emerald-300 font-bold text-emerald-700' : 'bg-slate-50 border-slate-200 text-slate-600'}`}>
                                  {b.position}. {b.name}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {/* Rankings History Chart */}
                  {rankingsHistory.length > 1 && (() => {
                    const query = selectedQueryRanking.query;
                    const engines = selectedQueryRanking.engines;
                    const chartData = rankingsHistory.map(h => {
                      const point = { date: new Date(h.date).toLocaleDateString(dateLocale, {day: '2-digit', month: '2-digit'}) };
                      let hasData = false;
                      h.rankings.filter(r => r.query === query).forEach(r => {
                        if (r.target_position != null) {
                          point[r.engine] = r.target_position;
                          hasData = true;
                        }
                      });
                      return hasData ? point : null;
                    }).filter(Boolean);
                    if (chartData.length < 2) return null;
                    const allEngines = [...new Set(chartData.flatMap(d => Object.keys(d).filter(k => k !== 'date')))];
                    const maxPos = Math.max(...chartData.flatMap(d => allEngines.map(e => d[e]).filter(Boolean)), 5);
                    return (
                      <div className="mt-4 pt-4 border-t border-slate-200">
                        <h4 className="text-sm font-bold text-[#2F2E2F] mb-3 flex items-center gap-2">
                          <TrendingUp className="w-4 h-4 text-[#1BB594]" /> {t('geo.динамика_позиции')}
                        </h4>
                        <ResponsiveContainer width="100%" height={220}>
                          <LineChart data={chartData} margin={{top: 5, right: 10, left: 0, bottom: 5}}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E2DDD8" />
                            <XAxis dataKey="date" tick={{fontSize: 11}} />
                            <YAxis reversed domain={[1, maxPos + 1]} tick={{fontSize: 11}} width={35} />
                            <Tooltip contentStyle={{fontSize: 12}} formatter={(val, name) => [`#${val}`, name]} />
                            <Legend wrapperStyle={{fontSize: 11}} />
                            {allEngines.map(eng => (
                              <Line key={eng} type="monotone" dataKey={eng} stroke={AI_ENGINE_COLORS[eng] || '#94A3B8'} strokeWidth={2} dot={{r: 3}} connectNulls />
                            ))}
                          </LineChart>
                        </ResponsiveContainer>
                        <p className="text-[10px] text-slate-400 mt-1 text-center">{t('geo.позиция_1_лучше')}</p>
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}
            {/* ─── Products ─── */}
            {monitor && (
              <div className="mb-12 bg-white border border-[#E2DDD8] p-6 sm:p-8" data-testid="products-section">
                <h2 className="text-2xl sm:text-3xl tracking-tight font-bold text-[#2F2E2F] mb-6">{t('geo.товары_услуги')}</h2>
                <div className="space-y-2 mb-4">
                  {(monitor.products || []).map((product) => (
                    <div key={product.id} className="flex items-center gap-3 p-3 bg-slate-50 border border-[#E2DDD8] cursor-pointer hover:bg-slate-100 hover:border-[#1BB594]/30 transition-colors" onClick={() => handleProductClick(product.name)}>
                      <Package className="w-4 h-4 text-[#1BB594] flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <span className="font-semibold text-sm text-slate-800">{product.name}</span>
                        {product.price && <span className="text-xs font-medium text-[#1BB594] ml-2">{product.price}</span>}
                        {product.city && <span className="text-xs text-slate-500 ml-2">{product.city}</span>}
                        {product.url && product.url !== 'not_found' && <span className="text-xs text-slate-400 ml-2 truncate">{product.url}</span>}
                        {product.description && <p className="text-xs text-slate-500 mt-0.5 truncate">{product.description}</p>}
                      </div>
                      <button onClick={(e) => { e.stopPropagation(); handleRemoveProduct(product.id); }} className="text-slate-400 hover:text-red-500 transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="p-4 border border-dashed border-[#E2DDD8] bg-[#F5F3EE]/30">
                  <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400 mb-3">{t('geo.добавить_товар')}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                    <input type="text" value={newProduct.name} onChange={(e) => setNewProduct({...newProduct, name: e.target.value})} placeholder={t('geo.название_товара') + ' *'} className="px-3 py-2 border border-[#E2DDD8] text-sm focus:outline-none focus:border-[#1BB594]" />
                    <input type="url" value={newProduct.url} onChange={(e) => setNewProduct({...newProduct, url: e.target.value})} placeholder={t('geo.url_товара_placeholder') + ' *'} className={`px-3 py-2 border text-sm focus:outline-none focus:border-[#1BB594] ${!newProduct.url.trim() && newProduct.name.trim() ? 'border-red-300 bg-red-50/30' : 'border-[#E2DDD8]'}`} />
                    <input type="text" value={newProduct.price} onChange={(e) => setNewProduct({...newProduct, price: e.target.value})} placeholder={t('geo.цена_placeholder')} className="px-3 py-2 border border-[#E2DDD8] text-sm focus:outline-none focus:border-[#1BB594]" />
                    <input type="text" value={newProduct.description} onChange={(e) => setNewProduct({...newProduct, description: e.target.value})} placeholder={t('geo.описание_placeholder')} className="px-3 py-2 border border-[#E2DDD8] text-sm focus:outline-none focus:border-[#1BB594]" />
                  </div>
                  <div className="mb-3">
                    <p className="text-xs text-slate-400 mb-2">{t('geo.локация_и_язык') || 'Location & query language'}</p>
                    <LocationSelector
                      value={newProduct.geo ? { country: newProduct.geo.country || '', region: newProduct.geo.region || '', city: newProduct.geo.city || '', language: newProduct.locale || '' } : {}}
                      onChange={(val) => setNewProduct({...newProduct, geo: { country: val.country, region: val.region, city: val.city }, locale: val.language || 'en'})}
                    />
                  </div>
                  <button onClick={handleAddProduct} disabled={!newProduct.name.trim() || !newProduct.url.trim() || addingProduct} className="rounded-none font-bold tracking-widest uppercase text-xs px-6 py-2.5 bg-[#1BB594] text-white hover:bg-[#159A7F] disabled:opacity-50 flex items-center gap-2">
                    <PlusCircle className="w-4 h-4" /> {addingProduct ? t('geo.добавление') : t('geo.добавить')}
                  </button>
                </div>
              </div>
            )}

            {/* ─── Competitor Analysis ─── */}
            {competitors && (competitors.top_competitors?.length > 0 || competitors.query_relevance?.length > 0 || competitors.top_resources?.length > 0) && (
              <div className="mb-12 bg-white border border-[#E2DDD8] p-6 sm:p-8" data-testid="competitor-analysis-section">
                <h2 className="text-2xl sm:text-3xl tracking-tight font-bold text-[#2F2E2F] mb-8">{t('geo.анализ_конкурентов')}</h2>
                <div className="grid md:grid-cols-2 gap-8 mb-8">
                  <div>
                    <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-[#1BB594]" /> {t('geo.топ_10_конкурентов')}
                    </h3>
                    {competitors.top_competitors?.length > 0 ? (
                      <div className="space-y-3">
                        {competitors.top_competitors.map((competitor, idx) => (
                          <div key={idx} className="flex justify-between items-center p-3 bg-slate-50 border border-slate-200">
                            <div className="flex items-center gap-3">
                              <span className="text-lg font-black text-[#1BB594] w-8">{idx + 1}</span>
                              <div>
                                <p className="font-semibold text-slate-900">{competitor.brand}</p>
                                {competitor.website && competitor.website !== 'not_found' && (
                                  <a href={competitor.website} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">{competitor.website}</a>
                                )}
                                {competitor.category && <span className="text-[10px] ml-2 text-slate-400">{competitor.category}</span>}
                              </div>
                            </div>
                            <span className="text-sm font-bold text-slate-600">{competitor.mentions}</span>
                          </div>
                        ))}
                      </div>
                    ) : <p className="text-slate-500">{t('geo.данные_отсутствуют')}</p>}
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                      <LinkIcon className="w-5 h-5 text-[#1BB594]" /> {t('geo.релевантность_запросов')}
                    </h3>
                    {competitors.query_relevance?.length > 0 ? (
                      <div className="space-y-2 max-h-[400px] overflow-y-auto">
                        {competitors.query_relevance.slice(0, 10).map((query, idx) => (
                          <div key={idx} className="p-2 bg-slate-50 border border-slate-200">
                            <div className="flex justify-between items-start mb-1">
                              <p className="text-sm text-slate-900 flex-1">{query.query}</p>
                              <span className="text-sm font-bold text-[#1BB594] ml-2">{query.relevance_score}%</span>
                            </div>
                            <p className="text-xs text-slate-500">{t('geo.упоминаний_из', { mentions: query.mentions, total: query.total_results })}</p>
                          </div>
                        ))}
                      </div>
                    ) : <p className="text-slate-500">{t('geo.данные_отсутствуют')}</p>}
                  </div>
                </div>
              </div>
            )}

            {/* ─── Brand Resources (SESSION 83) ─── */}
            {results && (
              <div className="mb-12 bg-white border border-[#E2DDD8] p-6 sm:p-8">
                <h2 className="text-2xl sm:text-3xl tracking-tight font-bold text-[#2F2E2F] mb-2">{t('geo.brand_resources', 'BRAND RESOURCES')}</h2>
                <p className="text-xs text-slate-400 mb-3">{t('geo.brand_resources_desc', 'Add your brand URLs on third-party platforms (Medium, YouTube, GitHub, etc.) to track citations from these sources.')}</p>
                <div className="flex flex-wrap gap-2 mb-3">
                  {brandResources.map((url, idx) => (
                    <span key={idx} className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-50 text-emerald-700 text-xs border border-emerald-200">
                      <LinkIcon className="w-3 h-3" />
                      {url.replace(/^https?:\/\/(www\.)?/, '').slice(0, 50)}{url.replace(/^https?:\/\/(www\.)?/, '').length > 50 ? '...' : ''}
                      <button onClick={() => handleRemoveBrandResource(idx)} className="text-emerald-400 hover:text-red-500 ml-1"><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <input type="url" value={newBrandResource} onChange={(e) => setNewBrandResource(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleAddBrandResource()} placeholder="https://medium.com/@yourbrand" className="flex-1 px-3 py-2 border border-[#E2DDD8] text-sm focus:outline-none focus:border-[#1BB594]" />
                  <button onClick={handleAddBrandResource} disabled={!newBrandResource.trim()} className="rounded-none font-bold tracking-widest uppercase text-xs px-5 py-2 bg-[#1BB594] text-white hover:bg-[#159A7F] disabled:opacity-50 flex items-center gap-1.5 whitespace-nowrap">
                    <PlusCircle className="w-3.5 h-3.5" /> {t('geo.добавить', 'ADD')}
                  </button>
                </div>
              </div>
            )}

            {/* ─── Resources Tabs: TOP 20 + Brand Citations (SESSION 83) ─── */}
            {(competitors?.top_resources?.length > 0 || results?.brand_citations?.length > 0) && (
              <div className="mb-12 bg-white border border-[#E2DDD8] p-6 sm:p-8" data-testid="resources-section">
                <div className="flex items-center gap-0 mb-6">
                  <button onClick={() => setResourcesTab('top20')} className={`font-bold tracking-widest uppercase text-xs px-5 py-2.5 border transition-colors ${resourcesTab === 'top20' ? 'bg-[#1BB594] text-white border-[#1BB594]' : 'bg-white text-slate-600 border-[#E2DDD8] hover:bg-slate-50'}`}>
                    {t('geo.топ_20_ресурсов', 'TOP 20 RESOURCES')}
                  </button>
                  {results?.brand_citations?.length > 0 && (
                    <button onClick={() => setResourcesTab('citations')} className={`font-bold tracking-widest uppercase text-xs px-5 py-2.5 border border-l-0 transition-colors ${resourcesTab === 'citations' ? 'bg-[#1BB594] text-white border-[#1BB594]' : 'bg-white text-slate-600 border-[#E2DDD8] hover:bg-slate-50'}`}>
                      {t('geo.brand_citations', 'BRAND CITATIONS')} <span className="ml-1 text-[10px] font-normal">({results.brand_citations.length})</span>
                    </button>
                  )}
                </div>

                {/* Tab 1: TOP 20 RESOURCES */}
                {resourcesTab === 'top20' && competitors?.top_resources?.length > 0 && (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    {competitors.top_resources.map((resource, idx) => (
                      <div key={idx} className="p-4 bg-slate-50 border border-slate-200 hover:border-[#1BB594]/30 transition-colors">
                        <div className="flex items-start justify-between mb-2">
                          <span className="text-sm font-black text-[#1BB594]">#{idx + 1}</span>
                          <span className="text-lg font-black text-[#1BB594]">{resource.mentions}</span>
                        </div>
                        <p className="text-sm font-semibold text-slate-900 mb-1 break-all">{resource.domain}</p>
                        <p className="text-xs text-slate-500">{resource.type || 'website'}</p>
                        {resource.engines?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {resource.engines.map(e => <span key={e} className="text-[9px] px-1 py-0.5" style={{ backgroundColor: `${AI_ENGINE_COLORS[e]}15`, color: AI_ENGINE_COLORS[e] }}>{e}</span>)}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Tab 2: BRAND CITATIONS */}
                {resourcesTab === 'citations' && results?.brand_citations?.length > 0 && (
                  <div>
                    <p className="text-xs text-slate-400 mb-4">{t('geo.brand_citations_desc', 'Pages where AI engines cite your brand with a direct link. Grouped by query.')}</p>
                    <div className="space-y-3">
                      {(() => {
                        const byQuery = {};
                        results.brand_citations.forEach(c => {
                          if (!byQuery[c.query]) byQuery[c.query] = [];
                          byQuery[c.query].push(c);
                        });
                        return Object.entries(byQuery).map(([query, cites]) => (
                          <div key={query} className="border border-slate-200 bg-slate-50 p-4">
                            <p className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-1.5">
                              <Target className="w-3.5 h-3.5 text-[#1BB594]" /> {query}
                              <span className="text-xs font-normal text-slate-400 ml-auto">{cites.length} {cites.length === 1 ? 'citation' : 'citations'}</span>
                            </p>
                            <div className="space-y-2">
                              {cites.map((cite, idx) => (
                                <div key={idx} className="flex items-start gap-3 bg-white p-3 border border-slate-100">
                                  <span className="text-[10px] font-bold px-1.5 py-0.5 whitespace-nowrap" style={{ backgroundColor: `${AI_ENGINE_COLORS[cite.engine] || '#94A3B8'}15`, color: AI_ENGINE_COLORS[cite.engine] || '#94A3B8' }}>{cite.engine}</span>
                                  <div className="flex-1 min-w-0">
                                    <a href={cite.url} target="_blank" rel="noopener noreferrer" className="text-sm text-[#1BB594] hover:underline break-all flex items-center gap-1">
                                      {cite.url.replace(/^https?:\/\/(www\.)?/, '').slice(0, 80)}{cite.url.replace(/^https?:\/\/(www\.)?/, '').length > 80 ? '...' : ''}
                                      <ExternalLink className="w-3 h-3 flex-shrink-0" />
                                    </a>
                                    {cite.title && <p className="text-xs text-slate-600 mt-0.5">{cite.title}</p>}
                                    {cite.snippet && <p className="text-xs text-slate-400 mt-0.5 line-clamp-2">{cite.snippet}</p>}
                                  </div>
                                  <span className={`text-[9px] px-1.5 py-0.5 whitespace-nowrap ${cite.match_type === 'domain' ? 'bg-emerald-50 text-emerald-600' : cite.match_type === 'title' ? 'bg-purple-50 text-purple-600' : 'bg-amber-50 text-amber-600'}`}>
                                    {cite.match_type}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ));
                      })()}
                    </div>
                    <p className="text-xs text-slate-400 mt-3">{results.brand_citations.length} total citations across all queries</p>
                  </div>
                )}
              </div>
            )}

            {/* ═══ AI TRAFFIC ANALYTICS ═══ */}
            <div className="mb-12 bg-white border border-[#E2DDD8] p-6 sm:p-8" data-testid="ai-traffic-section">
              <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
                <h2 className="text-2xl sm:text-3xl tracking-tight font-bold text-[#2F2E2F] flex items-center gap-2">
                  <BarChart3 className="w-6 h-6 text-[#1BB594]" /> AI Traffic
                </h2>
                <div className="flex gap-2">
                  {[7, 30, 90].map(d => (
                    <button key={d} onClick={() => setPixelPeriod(d)} className={`rounded-none font-bold tracking-widest uppercase text-xs px-3 py-2 border transition-colors ${pixelPeriod === d ? 'bg-[#1BB594] text-white border-[#1BB594]' : 'bg-white text-slate-700 border-[#E2DDD8] hover:bg-slate-50'}`}>
                      {d}d
                    </button>
                  ))}
                </div>
              </div>

              {pixelStats && pixelStats.total_visits > 0 ? (
                <>
                  {/* A) Metric Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                    <div className="bg-slate-50 border border-slate-200 p-4 text-center">
                      <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Total AI Visits</p>
                      <p className="text-3xl font-black text-[#1BB594]">{pixelStats.total_visits}</p>
                      <p className="text-[10px] text-slate-400">{pixelStats.period_days} days</p>
                    </div>
                    <div className="bg-slate-50 border border-slate-200 p-4 text-center">
                      <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Today</p>
                      <p className="text-3xl font-black text-[#2F2E2F]">
                        {(pixelStats.by_day && pixelStats.by_day.length > 0) ? pixelStats.by_day[pixelStats.by_day.length - 1].visits : 0}
                      </p>
                      <p className="text-[10px] text-slate-400">visits</p>
                    </div>
                    <div className="bg-slate-50 border border-slate-200 p-4 text-center">
                      <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Top Engine</p>
                      <p className="text-xl font-black text-[#2F2E2F]">
                        {(pixelStats.by_engine && pixelStats.by_engine.length > 0) ? pixelStats.by_engine[0].engine : '—'}
                      </p>
                      <p className="text-[10px] text-slate-400">
                        {(pixelStats.by_engine && pixelStats.by_engine.length > 0) ? pixelStats.by_engine[0].percent + '%' : ''}
                      </p>
                    </div>
                    <div className="bg-slate-50 border border-slate-200 p-4 text-center">
                      <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1"><Smartphone className="w-3 h-3 inline" /> Mobile</p>
                      <p className="text-3xl font-black text-[#2F2E2F]">
                        {(() => { var m = (pixelStats.by_device || []).find(function(d) { return d.device === 'mobile'; }); return m ? m.percent + '%' : '0%'; })()}
                      </p>
                      <p className="text-[10px] text-slate-400">of traffic</p>
                    </div>
                  </div>

                  {/* A2) v2 Engagement Metric Cards */}
                  {pixelStats.engagement && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                      <div className="bg-slate-50 border border-slate-200 p-4 text-center">
                        <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1"><Users className="w-3 h-3 inline" /> Unique Visitors</p>
                        <p className="text-3xl font-black text-[#2F2E2F]">{pixelStats.unique_visitors || 0}</p>
                        <p className="text-[10px] text-slate-400">{pixelStats.total_events || 0} events</p>
                      </div>
                      <div className="bg-slate-50 border border-slate-200 p-4 text-center">
                        <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1"><Timer className="w-3 h-3 inline" /> Avg Time</p>
                        <p className="text-3xl font-black text-[#2F2E2F]">
                          {pixelStats.engagement.avg_engagement_seconds > 60
                            ? Math.floor(pixelStats.engagement.avg_engagement_seconds / 60) + 'm ' + Math.round(pixelStats.engagement.avg_engagement_seconds % 60) + 's'
                            : Math.round(pixelStats.engagement.avg_engagement_seconds) + 's'}
                        </p>
                        <p className="text-[10px] text-slate-400">engagement</p>
                      </div>
                      <div className="bg-slate-50 border border-slate-200 p-4 text-center">
                        <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Bounce Rate</p>
                        <p className={'text-3xl font-black ' + (pixelStats.engagement.bounce_rate > 70 ? 'text-red-500' : pixelStats.engagement.bounce_rate > 40 ? 'text-amber-500' : 'text-[#1BB594]')}>
                          {pixelStats.engagement.bounce_rate}%
                        </p>
                        <p className="text-[10px] text-slate-400">1 page & {'<'}10s</p>
                      </div>
                      <div className="bg-slate-50 border border-slate-200 p-4 text-center">
                        <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1"><RotateCcw className="w-3 h-3 inline" /> Return Rate</p>
                        <p className="text-3xl font-black text-[#1BB594]">
                          {pixelStats.return_visitors ? pixelStats.return_visitors.return_rate : 0}%
                        </p>
                        <p className="text-[10px] text-slate-400">
                          {pixelStats.return_visitors ? pixelStats.return_visitors.return_visits : 0} return / {pixelStats.return_visitors ? pixelStats.return_visitors.new_visits : 0} new
                        </p>
                      </div>
                    </div>
                  )}

                  {/* B) Daily Visits Chart */}
                  {pixelStats.by_day && pixelStats.by_day.length > 1 && (
                    <div className="mb-8">
                      <h3 className="text-lg font-bold text-[#2F2E2F] mb-4">Visits by Day</h3>
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={pixelStats.by_day}>
                          <CartesianGrid strokeDasharray="0" stroke="#E2DDD8" opacity={0.3} />
                          <XAxis dataKey="date" stroke="#64748B" style={{ fontSize: '10px' }} tickFormatter={function(v) { return v.slice(5); }} />
                          <YAxis stroke="#64748B" style={{ fontSize: '10px' }} />
                          <Tooltip contentStyle={{ background: '#fff', border: '1px solid #E2DDD8', borderRadius: 0 }} />
                          <Bar dataKey="visits" fill="#1BB594" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {/* ═══ ACCORDION: AI Engines & Device ═══ */}
                  <div className="border border-slate-200 mb-3">
                    <button onClick={function() { togglePixelSection('engines'); }} className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left">
                      <span className="text-sm font-bold text-slate-700 uppercase tracking-wider">AI Engines & Device</span>
                      {pixelSections.engines ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                    </button>
                    {pixelSections.engines && (
                      <div className="p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {pixelStats.by_engine && pixelStats.by_engine.length > 0 && (
                            <div>
                              <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-3">By AI Engine</h3>
                              <div className="space-y-2">
                                {pixelStats.by_engine.map(function(e) {
                                  var color = AI_ENGINE_COLORS[e.engine] || '#64748B';
                                  return (
                                    <div key={e.engine} className="flex items-center gap-3">
                                      <div className="w-3 h-3 flex-shrink-0" style={{ backgroundColor: color }} />
                                      <span className="text-sm text-slate-700 w-24 flex-shrink-0">{e.engine}</span>
                                      <div className="flex-1 bg-slate-100 h-5 relative">
                                        <div className="h-5 absolute left-0 top-0" style={{ width: e.percent + '%', backgroundColor: color, opacity: 0.7 }} />
                                      </div>
                                      <span className="text-xs font-bold text-slate-600 w-16 text-right">{e.visits} ({e.percent}%)</span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          {pixelStats.by_device && pixelStats.by_device.length > 0 && (
                            <div>
                              <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-3">By Device</h3>
                              <div className="space-y-2">
                                {pixelStats.by_device.map(function(d) {
                                  var icon = d.device === 'mobile' ? <Smartphone className="w-4 h-4" /> : d.device === 'tablet' ? <Tablet className="w-4 h-4" /> : <Monitor className="w-4 h-4" />;
                                  return (
                                    <div key={d.device} className="flex items-center gap-3">
                                      <span className="text-slate-500">{icon}</span>
                                      <span className="text-sm text-slate-700 w-20 capitalize">{d.device}</span>
                                      <div className="flex-1 bg-slate-100 h-5 relative">
                                        <div className="h-5 absolute left-0 top-0 bg-slate-400" style={{ width: d.percent + '%' }} />
                                      </div>
                                      <span className="text-xs font-bold text-slate-600 w-16 text-right">{d.visits} ({d.percent}%)</span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* ═══ ACCORDION: Detection, Countries, Languages ═══ */}
                  <div className="border border-slate-200 mb-3">
                    <button onClick={function() { togglePixelSection('geo'); }} className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left">
                      <span className="text-sm font-bold text-slate-700 uppercase tracking-wider">Detection · Countries · Languages</span>
                      {pixelSections.geo ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                    </button>
                    {pixelSections.geo && (
                      <div className="p-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                          {pixelStats.by_detection_method && pixelStats.by_detection_method.length > 0 && (
                            <div>
                              <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-3">Detection</h3>
                              {pixelStats.by_detection_method.map(function(m) {
                                return (
                                  <div key={m.method} className="flex justify-between text-sm py-1">
                                    <span className="text-slate-600 capitalize">{m.method === 'referrer' ? 'HTTP Referrer' : m.method === 'utm' ? 'UTM Parameter' : m.method === 'return' ? 'Return Visit (Cookie)' : m.method}</span>
                                    <span className="font-bold text-[#1BB594]">{m.percent}%</span>
                                  </div>
                                );
                              })}
                              <p className="text-[10px] text-slate-400 mt-2">UTM catches visits invisible to referrer-only tracking</p>
                            </div>
                          )}

                    {/* G) Top Countries */}
                    {pixelStats.by_country && pixelStats.by_country.length > 0 && (
                      <div>
                        <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-3"><Globe className="w-3 h-3 inline" /> Countries</h3>
                        {pixelStats.by_country.slice(0, 7).map(function(c) {
                          return (
                            <div key={c.country} className="flex justify-between text-sm py-1 border-b border-slate-100">
                              <span className="text-slate-600">{c.country}</span>
                              <span className="font-bold text-slate-700">{c.visits} <span className="text-[10px] text-slate-400">({c.percent}%)</span></span>
                            </div>
                          );
                        })}
                      </div>
                    )}

                          {/* Languages */}
                          {pixelStats.by_language && pixelStats.by_language.length > 0 && (
                            <div>
                              <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-3">Languages</h3>
                              {pixelStats.by_language.slice(0, 7).map(function(l) {
                                return (
                                  <div key={l.language} className="flex justify-between text-sm py-1 border-b border-slate-100">
                                    <span className="text-slate-600">{l.language}</span>
                                    <span className="font-bold text-slate-700">{l.visits} <span className="text-[10px] text-slate-400">({l.percent}%)</span></span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* ═══ ACCORDION: Top Landing Pages ═══ */}
                  {pixelStats.by_page && pixelStats.by_page.length > 0 && (
                    <div className="border border-slate-200 mb-3">
                      <button onClick={function() { togglePixelSection('pages'); }} className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left">
                        <span className="text-sm font-bold text-slate-700 uppercase tracking-wider">Top Landing Pages ({pixelStats.by_page.length})</span>
                        {pixelSections.pages ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                      </button>
                      {pixelSections.pages && (
                        <div className="border-t border-slate-200">
                          {pixelStats.by_page.slice(0, 10).map(function(p, i) {
                            return (
                              <div key={i} className={'flex justify-between items-center px-4 py-2 text-sm' + (i % 2 === 0 ? ' bg-slate-50' : '')}>
                                <span className="text-slate-600 truncate" style={{maxWidth: '70%'}}>{p.page}</span>
                                <span className="font-bold text-[#1BB594]">{p.visits} <span className="text-[10px] text-slate-400">({p.percent}%)</span></span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  {/* ═══ ACCORDION: Visitor Behavior ═══ */}
                  <div className="border border-slate-200 mb-3">
                    <button onClick={function() { togglePixelSection('behavior'); }} className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left">
                      <span className="text-sm font-bold text-slate-700 uppercase tracking-wider flex items-center gap-2">
                        <MousePointer2 className="w-4 h-4 text-[#1BB594]" /> Visitor Behavior
                        <span className="text-[10px] font-normal text-slate-400 normal-case">
                          {pixelStats.sessions ? pixelStats.sessions.total_sessions + ' sessions' : ''}
                          {pixelStats.total_clicks ? ' · ' + pixelStats.total_clicks + ' clicks' : ''}
                        </span>
                      </span>
                      {pixelSections.behavior ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                    </button>
                    {pixelSections.behavior && (
                      <div className="p-4">
                        {/* Sessions metrics */}
                        {pixelStats.sessions && pixelStats.sessions.total_sessions > 0 && (
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                            <div className="text-center">
                              <p className="text-lg font-bold text-[#2F2E2F]">{pixelStats.sessions.total_sessions}</p>
                              <p className="text-[10px] text-slate-400 uppercase">Unique Sessions</p>
                            </div>
                            <div className="text-center">
                              <p className="text-lg font-bold text-[#2F2E2F]">{pixelStats.engagement ? pixelStats.engagement.avg_pages_per_session : pixelStats.sessions.avg_pages_per_session}</p>
                              <p className="text-[10px] text-slate-400 uppercase">Avg Pages / Session</p>
                            </div>
                            <div className="text-center">
                              <p className="text-lg font-bold text-[#2F2E2F]">{pixelStats.engagement ? pixelStats.engagement.avg_scroll_depth + '%' : '—'}</p>
                              <p className="text-[10px] text-slate-400 uppercase">Avg Scroll Depth</p>
                            </div>
                            <div className="text-center">
                              <p className="text-lg font-bold text-[#2F2E2F]">{pixelStats.engagement ? pixelStats.engagement.avg_clicks_per_session : '—'}</p>
                              <p className="text-[10px] text-slate-400 uppercase">Avg Clicks / Session</p>
                            </div>
                          </div>
                        )}

                        {/* Top Clicked Elements */}
                        {pixelStats.top_clicks && pixelStats.top_clicks.length > 0 && (
                          <div className="mb-6">
                            <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-3">Top Clicked Elements</h3>
                            <div className="border border-slate-200">
                              <div className="flex items-center px-4 py-2 text-[10px] font-bold text-slate-500 uppercase bg-slate-100 border-b border-slate-200">
                                <span className="w-16">Tag</span>
                                <span className="flex-1">Text</span>
                                <span className="w-16 text-right">Clicks</span>
                              </div>
                              {pixelStats.top_clicks.slice(0, 10).map(function(c, i) {
                                return (
                                  <div key={i} className={'flex items-center px-4 py-2 text-sm' + (i % 2 === 0 ? ' bg-slate-50' : '')} title={c.href || ''}>
                                    <span className="w-16 text-[10px] font-mono text-slate-400">&lt;{(c.tag || '').toLowerCase()}&gt;</span>
                                    <span className="flex-1 text-slate-700 truncate">{c.text || '(no text)'}</span>
                                    <span className="w-16 text-right font-bold text-[#1BB594]">{c.count}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Scroll Depth Distribution */}
                        {pixelStats.scroll_distribution && pixelStats.scroll_distribution.length > 0 && (
                          <div>
                            <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-3">Scroll Depth</h3>
                            <div className="flex items-end gap-1 h-32">
                              {pixelStats.scroll_distribution.map(function(s) {
                                var maxCount = Math.max.apply(null, pixelStats.scroll_distribution.map(function(x) { return x.count; }));
                                var heightPct = maxCount > 0 ? (s.count / maxCount * 100) : 0;
                                return (
                                  <div key={s.depth} className="flex-1 flex flex-col items-center">
                                    <span className="text-[10px] font-bold text-[#1BB594] mb-1">{s.count}</span>
                                    <div className="w-full bg-[#1BB594] rounded-t" style={{ height: heightPct + '%', minHeight: s.count > 0 ? '4px' : '0' , opacity: 0.6 + (s.depth / 100 * 0.4) }} />
                                    <span className="text-[10px] text-slate-500 mt-1">{s.depth}%</span>
                                  </div>
                                );
                              })}
                            </div>
                            <p className="text-[10px] text-slate-400 mt-1 text-center">25% → 50% → 75% → 100%</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* ═══ ACCORDION: Conversions & ROI ═══ */}
                  {((pixelStats.conversions && pixelStats.conversions.total_conversions > 0) || (pixelStats.conversions && pixelStats.conversions.total_conversions === 0 && pixelStats.total_visits > 0)) && (
                    <div className="border border-slate-200 mb-3">
                      <button onClick={function() { togglePixelSection('conversions'); }} className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left">
                        <span className="text-sm font-bold text-slate-700 uppercase tracking-wider flex items-center gap-2">
                          <Target className="w-4 h-4 text-[#1BB594]" /> Conversions & ROI
                          {pixelStats.conversions && pixelStats.conversions.total_conversions > 0 && (
                            <span className="text-[10px] font-normal text-slate-400 normal-case">
                              {pixelStats.conversions.total_conversions} conv · {pixelStats.conversions.conversion_rate}%
                            </span>
                          )}
                        </span>
                        {pixelSections.conversions ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                      </button>
                      {pixelSections.conversions && (
                        <div className="p-4">
                          {pixelStats.conversions.total_conversions > 0 ? (
                            <>
                              {/* Funnel */}
                              <div className="grid grid-cols-3 gap-2 mb-4">
                                <div className="bg-blue-50 rounded-lg p-3 text-center">
                                  <p className="text-xl font-bold text-blue-600">{pixelStats.conversions.funnel.visits}</p>
                                  <p className="text-[10px] text-slate-500 uppercase">AI Visits</p>
                                </div>
                                <div className="bg-green-50 rounded-lg p-3 text-center">
                                  <p className="text-xl font-bold text-green-600">{pixelStats.conversions.funnel.conversions}</p>
                                  <p className="text-[10px] text-slate-500 uppercase">Conversions</p>
                                </div>
                                <div className="bg-amber-50 rounded-lg p-3 text-center">
                                  <p className="text-xl font-bold text-amber-600">${pixelStats.conversions.funnel.revenue.toFixed(2)}</p>
                                  <p className="text-[10px] text-slate-500 uppercase">Revenue</p>
                                </div>
                              </div>

                              {/* Conversion metrics row */}
                              <div className="grid grid-cols-2 gap-3 mb-4">
                                <div className="text-center">
                                  <p className="text-lg font-bold text-[#1BB594]">{pixelStats.conversions.conversion_rate}%</p>
                                  <p className="text-[10px] text-slate-400 uppercase">Conversion Rate</p>
                                </div>
                                <div className="text-center">
                                  <p className="text-lg font-bold text-[#2F2E2F]">
                                    {pixelStats.conversions.avg_days_to_convert !== null ? pixelStats.conversions.avg_days_to_convert + 'd' : '—'}
                                  </p>
                                  <p className="text-[10px] text-slate-400 uppercase">Avg Days to Convert</p>
                                </div>
                              </div>

                              {/* Conversions by engine */}
                              {pixelStats.conversions.by_engine && pixelStats.conversions.by_engine.length > 0 && (
                                <div className="mb-4">
                                  <h4 className="text-xs font-semibold text-slate-500 uppercase mb-2">By AI Engine</h4>
                                  {pixelStats.conversions.by_engine.map(function(e) {
                                    return (
                                      <div key={e.engine} className="flex items-center justify-between py-1 text-sm">
                                        <span className="font-medium" style={{color: AI_ENGINE_COLORS[e.engine] || '#6B7280'}}>{e.engine}</span>
                                        <span className="text-slate-600">
                                          {e.conversions} conv ({e.conversion_rate}%) · ${e.revenue.toFixed(2)}
                                        </span>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}

                              {/* Conversions by event type */}
                              {pixelStats.conversions.by_event && pixelStats.conversions.by_event.length > 0 && (
                                <div className="mb-4">
                                  <h4 className="text-xs font-semibold text-slate-500 uppercase mb-2">By Event Type</h4>
                                  {pixelStats.conversions.by_event.map(function(e) {
                                    return (
                                      <div key={e.event} className="flex items-center justify-between py-1 text-sm">
                                        <span className="font-medium text-slate-700 capitalize">{e.event}</span>
                                        <span className="text-slate-500">{e.count} · ${e.revenue.toFixed(2)}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}

                              {/* Top converting pages */}
                              {pixelStats.conversions.by_page && pixelStats.conversions.by_page.length > 0 && (
                                <div className="mb-4">
                                  <h4 className="text-xs font-semibold text-slate-500 uppercase mb-2">Top Converting Pages</h4>
                                  {pixelStats.conversions.by_page.slice(0, 5).map(function(p, i) {
                                    return (
                                      <div key={i} className="flex items-center justify-between py-1 text-sm">
                                        <span className="text-slate-600 truncate max-w-[200px]" title={p.page}>{p.page || '/'}</span>
                                        <span className="text-slate-500">{p.conversions} · ${p.revenue.toFixed(2)}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}

                              {/* First-touch attribution (v2) */}
                              {pixelStats.conversions.by_first_touch && pixelStats.conversions.by_first_touch.length > 0 && (
                                <div className="mb-4 bg-purple-50 rounded-lg p-4">
                                  <h4 className="text-xs font-semibold text-purple-700 uppercase mb-2">First-Touch Attribution</h4>
                                  <p className="text-[10px] text-purple-500 mb-2">Which AI engine FIRST brought the user — even if they converted later via a different source</p>
                                  {pixelStats.conversions.by_first_touch.map(function(e) {
                                    return (
                                      <div key={e.engine} className="flex items-center justify-between py-1 text-sm">
                                        <span className="font-medium" style={{color: AI_ENGINE_COLORS[e.engine] || '#6B7280'}}>{e.engine}</span>
                                        <span className="text-slate-600">
                                          {e.conversions} conv · ${e.revenue.toFixed(2)}
                                        </span>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}

                              {/* ROI Block */}
                              {pixelStats.roi && (
                                <div className={'rounded-lg p-4 mt-4 ' + (pixelStats.roi.roi_percent > 0 ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200')}>
                                  <h4 className="text-xs font-semibold text-slate-600 uppercase mb-2">ROI ({pixelStats.roi.period_days} days)</h4>
                                  <div className="grid grid-cols-3 gap-2 text-center">
                                    <div>
                                      <p className="text-sm font-bold text-green-600">${pixelStats.roi.revenue.toFixed(2)}</p>
                                      <p className="text-[9px] text-slate-400">Revenue</p>
                                    </div>
                                    <div>
                                      <p className="text-sm font-bold text-red-500">${pixelStats.roi.platform_cost.toFixed(2)}</p>
                                      <p className="text-[9px] text-slate-400">Platform Cost</p>
                                    </div>
                                    <div>
                                      <p className={'text-lg font-black ' + (pixelStats.roi.roi_percent > 0 ? 'text-green-600' : 'text-red-500')}>
                                        {pixelStats.roi.roi_percent > 0 ? '+' : ''}{pixelStats.roi.roi_percent}%
                                      </p>
                                      <p className="text-[9px] text-slate-400">ROI</p>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Safari ITP warning */}
                              <p className="text-[10px] text-slate-400 mt-3">
                                Safari limits attribution cookies to 7 days. Other browsers: 30 days.
                              </p>
                            </>
                          ) : (
                            /* No conversions yet — setup hint */
                            <div className="bg-blue-50 rounded-lg p-4">
                              <h4 className="text-sm font-bold text-blue-800 mb-2">Track Conversions & ROI</h4>
                              <p className="text-xs text-blue-600 mb-3">
                                Add one line of code on your "Thank You" page to measure how AI traffic converts to revenue.
                              </p>
                              <div className="bg-slate-900 text-green-400 p-3 font-mono text-xs rounded">
                                {"window.arpaiConversion('purchase', 99.99)"}
                              </div>
                              <p className="text-[10px] text-blue-400 mt-2">
                                Supported events: purchase, signup, lead, contact, download
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Pixel Status */}
                  <div className="flex items-center gap-2 text-xs text-slate-400 mt-4 pt-4 border-t border-slate-200">
                    <span className={'w-2 h-2 rounded-full ' + (pixelStats.pixel_status === 'active' ? 'bg-green-500' : pixelStats.pixel_status === 'waiting' ? 'bg-yellow-500' : 'bg-red-500')} />
                    <span>
                      {pixelStats.pixel_status === 'active' ? 'Pixel active' : pixelStats.pixel_status === 'waiting' ? 'Waiting for AI traffic' : 'No recent data'}
                      {pixelStats.last_event && (' — last event ' + new Date(pixelStats.last_event).toLocaleDateString())}
                    </span>
                  </div>
                </>
              ) : (
                /* No data — show pixel installation instructions */
                <div className="text-center py-8">
                  <Eye className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-bold text-[#2F2E2F] mb-2">Track AI Traffic to Your Website</h3>
                  <p className="text-sm text-slate-500 mb-6 max-w-md mx-auto">
                    Install our lightweight pixel ({'<'}3KB) to see which AI assistants send visitors to your site, from which countries, on which devices.
                  </p>
                  <div className="bg-slate-900 text-green-400 p-4 text-left font-mono text-sm mb-4 max-w-lg mx-auto relative">
                    <code>{'<script src="https://arpai-geo.com/api/pixel/script/' + monitorId + '"></script>'}</code>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText('<script src="https://arpai-geo.com/api/pixel/script/' + monitorId + '"></script>');
                        setPixelCodeCopied(true);
                        setTimeout(() => setPixelCodeCopied(false), 2000);
                      }}
                      className="absolute top-2 right-2 text-slate-400 hover:text-white"
                      title="Copy to clipboard"
                    >
                      {pixelCodeCopied ? <CheckCircle className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-slate-400">
                    Add this snippet before {'</body>'} on your website pages, or use Google Tag Manager (Custom HTML Tag).
                  </p>
                  {pixelStats && pixelStats.pixel_status && (
                    <div className="flex items-center justify-center gap-2 text-xs text-slate-400 mt-4">
                      <span className={'w-2 h-2 rounded-full ' + (pixelStats.pixel_status === 'active' ? 'bg-green-500' : pixelStats.pixel_status === 'waiting' ? 'bg-yellow-500' : 'bg-red-500')} />
                      <span>{pixelStats.pixel_status === 'waiting' ? 'Pixel installed, waiting for AI traffic' : 'No data yet — install the pixel'}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ─── Site Audit v3 ─── */}
            <div className="mb-12 bg-white border border-[#E2DDD8] p-6 sm:p-8" data-testid="audit-v3-section">
              {/* Header */}
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-2xl sm:text-3xl tracking-tight font-bold text-[#2F2E2F]">Site Audit</h2>
                  {auditV3 && (
                    <p className="text-xs text-slate-400 mt-1">
                      {auditV3.meta?.check_count || auditChecks.length} checks · {((auditV3.meta?.scan_time_ms || 0) / 1000).toFixed(1)}s · {new Date(auditV3.created_at).toLocaleDateString(dateLocale)}
                    </p>
                  )}
                </div>
                <button onClick={runAuditV3} disabled={auditV3Loading}
                  className="rounded-none font-bold tracking-widest uppercase text-xs px-5 py-2.5 bg-[#1BB594] text-white hover:bg-[#159A7F] disabled:opacity-50 flex items-center gap-2">
                  {auditV3Loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                  {auditV3Loading ? 'Scanning...' : (auditV3 ? 'Re-scan' : 'Run audit')}
                </button>
              </div>

              {auditV3 ? (
                <div>
                  {/* Diagnosis */}
                  <p className="text-sm text-slate-600 mb-6 leading-relaxed">{_auditDiagnosis(auditV3.summary)}</p>

                  {/* Overall score */}
                  <div className="flex items-center gap-4 mb-6">
                    <div className="text-5xl font-black" style={{ color: (auditV3.summary?.overall_score || 0) >= 75 ? '#1BB594' : (auditV3.summary?.overall_score || 0) >= 50 ? '#F59E0B' : '#EF4444' }}>
                      {auditV3.summary?.overall_score || 0}<span className="text-2xl text-slate-400">%</span>
                    </div>
                    <div className="text-xs text-slate-500">
                      <p>{auditV3.summary?.total_issues || 0} issues found</p>
                      <p>{auditPassed.length} checks passed</p>
                    </div>
                  </div>

                  {/* Four score bars */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                    {Object.entries(AUDIT_BLOCKS).map(([key, block]) => {
                      const data = auditV3.summary?.blocks?.[key] || {};
                      const score = data.score || 0;
                      const barColor = score >= 75 ? '#1BB594' : score >= 50 ? '#F59E0B' : '#EF4444';
                      return (
                        <div key={key}>
                          <div className="flex justify-between items-baseline mb-1">
                            <p className="text-sm font-semibold text-slate-800">{block.label}</p>
                            <span className="text-lg font-black" style={{ color: barColor }}>{score}%</span>
                          </div>
                          <div className="w-full h-2 bg-slate-100 overflow-hidden">
                            <div className="h-full transition-all duration-500" style={{ width: `${score}%`, backgroundColor: barColor }} />
                          </div>
                          <p className="text-[10px] text-slate-400 mt-0.5">{data.fail || 0} issues · {data.pass || 0} passed</p>
                        </div>
                      );
                    })}
                  </div>

                  {/* ── Fix now (critical) ── */}
                  {auditFixNow.length > 0 && (
                    <div className="mb-4">
                      <button className="flex items-center gap-2 w-full text-left py-3" onClick={() => setAuditV3Sections(s => ({...s, fix_now: !s.fix_now}))}>
                        <span className="w-2 h-2 bg-red-500 flex-shrink-0" />
                        <span className="text-sm font-bold text-slate-900">Fix now</span>
                        <span className="text-xs text-red-500 font-semibold">{auditFixNow.length}</span>
                        {auditV3Sections.fix_now ? <ChevronUp className="w-4 h-4 text-slate-400 ml-auto" /> : <ChevronDown className="w-4 h-4 text-slate-400 ml-auto" />}
                      </button>
                      {auditV3Sections.fix_now && (
                        <div className="space-y-2 pl-4">
                          {auditFixNow.map(c => {
                            const isExpanded = expandedCheck === c.id;
                            const who = WHO_BADGES[c.who] || WHO_BADGES.developer;
                            const blockInfo = AUDIT_BLOCKS[c.block];
                            return (
                              <div key={c.id} className="border border-red-200 bg-red-50/50">
                                <div className="p-3 flex items-start gap-3 cursor-pointer" onClick={() => setExpandedCheck(isExpanded ? null : c.id)}>
                                  <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold text-slate-900">{c.user_title}</p>
                                    <div className="flex gap-2 mt-1 flex-wrap">
                                      <span className={`text-[9px] px-1.5 py-0.5 font-bold uppercase ${who.cls}`}>{who.label}</span>
                                      {blockInfo && <span className="text-[9px] px-1.5 py-0.5 bg-slate-100 text-slate-500">{blockInfo.label}</span>}
                                      <span className="text-[9px] px-1.5 py-0.5 bg-red-100 text-red-600 font-bold uppercase">{c.status}</span>
                                    </div>
                                  </div>
                                  {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                                </div>
                                {isExpanded && (
                                  <div className="px-3 pb-3 border-t border-red-200 pt-2 ml-7">
                                    {c.user_detail && <p className="text-xs text-slate-600 mb-2">{c.user_detail}</p>}
                                    {FIX_HINTS[c.id] && (
                                      <div className="p-2 bg-white border border-slate-200 mt-1">
                                        <p className="text-[10px] uppercase tracking-wider text-[#1BB594] font-bold mb-1">How to fix</p>
                                        <p className="text-xs text-slate-700">{FIX_HINTS[c.id]}</p>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── Should fix (high) ── */}
                  {auditShouldFix.length > 0 && (
                    <div className="mb-4">
                      <button className="flex items-center gap-2 w-full text-left py-3" onClick={() => setAuditV3Sections(s => ({...s, should_fix: !s.should_fix}))}>
                        <span className="w-2 h-2 bg-amber-500 flex-shrink-0" />
                        <span className="text-sm font-bold text-slate-900">Should fix</span>
                        <span className="text-xs text-amber-600 font-semibold">{auditShouldFix.length}</span>
                        {auditV3Sections.should_fix ? <ChevronUp className="w-4 h-4 text-slate-400 ml-auto" /> : <ChevronDown className="w-4 h-4 text-slate-400 ml-auto" />}
                      </button>
                      {auditV3Sections.should_fix && (
                        <div className="space-y-2 pl-4">
                          {auditShouldFix.map(c => {
                            const isExpanded = expandedCheck === c.id;
                            const who = WHO_BADGES[c.who] || WHO_BADGES.developer;
                            const blockInfo = AUDIT_BLOCKS[c.block];
                            return (
                              <div key={c.id} className="border border-amber-200 bg-amber-50/50">
                                <div className="p-3 flex items-start gap-3 cursor-pointer" onClick={() => setExpandedCheck(isExpanded ? null : c.id)}>
                                  <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold text-slate-900">{c.user_title}</p>
                                    <div className="flex gap-2 mt-1 flex-wrap">
                                      <span className={`text-[9px] px-1.5 py-0.5 font-bold uppercase ${who.cls}`}>{who.label}</span>
                                      {blockInfo && <span className="text-[9px] px-1.5 py-0.5 bg-slate-100 text-slate-500">{blockInfo.label}</span>}
                                      <span className={`text-[9px] px-1.5 py-0.5 font-bold uppercase ${c.status === 'fail' ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-600'}`}>{c.status}</span>
                                    </div>
                                  </div>
                                  {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                                </div>
                                {isExpanded && (
                                  <div className="px-3 pb-3 border-t border-amber-200 pt-2 ml-7">
                                    {c.user_detail && <p className="text-xs text-slate-600 mb-2">{c.user_detail}</p>}
                                    {FIX_HINTS[c.id] && (
                                      <div className="p-2 bg-white border border-slate-200 mt-1">
                                        <p className="text-[10px] uppercase tracking-wider text-[#1BB594] font-bold mb-1">How to fix</p>
                                        <p className="text-xs text-slate-700">{FIX_HINTS[c.id]}</p>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── Nice to have (medium) ── */}
                  {auditNiceToHave.length > 0 && (
                    <div className="mb-4">
                      <button className="flex items-center gap-2 w-full text-left py-3" onClick={() => setAuditV3Sections(s => ({...s, nice_to_have: !s.nice_to_have}))}>
                        <span className="w-2 h-2 bg-blue-500 flex-shrink-0" />
                        <span className="text-sm font-bold text-slate-900">Nice to have</span>
                        <span className="text-xs text-blue-500 font-semibold">{auditNiceToHave.length}</span>
                        {auditV3Sections.nice_to_have ? <ChevronUp className="w-4 h-4 text-slate-400 ml-auto" /> : <ChevronDown className="w-4 h-4 text-slate-400 ml-auto" />}
                      </button>
                      {auditV3Sections.nice_to_have && (
                        <div className="space-y-2 pl-4">
                          {auditNiceToHave.map(c => {
                            const isExpanded = expandedCheck === c.id;
                            const who = WHO_BADGES[c.who] || WHO_BADGES.developer;
                            const blockInfo = AUDIT_BLOCKS[c.block];
                            return (
                              <div key={c.id} className="border border-slate-200 bg-slate-50/50">
                                <div className="p-3 flex items-start gap-3 cursor-pointer" onClick={() => setExpandedCheck(isExpanded ? null : c.id)}>
                                  <Circle className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold text-slate-900">{c.user_title}</p>
                                    <div className="flex gap-2 mt-1 flex-wrap">
                                      <span className={`text-[9px] px-1.5 py-0.5 font-bold uppercase ${who.cls}`}>{who.label}</span>
                                      {blockInfo && <span className="text-[9px] px-1.5 py-0.5 bg-slate-100 text-slate-500">{blockInfo.label}</span>}
                                      <span className={`text-[9px] px-1.5 py-0.5 font-bold uppercase ${c.status === 'fail' ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-600'}`}>{c.status}</span>
                                    </div>
                                  </div>
                                  {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                                </div>
                                {isExpanded && (
                                  <div className="px-3 pb-3 border-t border-slate-200 pt-2 ml-7">
                                    {c.user_detail && <p className="text-xs text-slate-600 mb-2">{c.user_detail}</p>}
                                    {FIX_HINTS[c.id] && (
                                      <div className="p-2 bg-white border border-slate-200 mt-1">
                                        <p className="text-[10px] uppercase tracking-wider text-[#1BB594] font-bold mb-1">How to fix</p>
                                        <p className="text-xs text-slate-700">{FIX_HINTS[c.id]}</p>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── What's working ── */}
                  {auditPassed.length > 0 && (
                    <div className="mb-4">
                      <button className="flex items-center gap-2 w-full text-left py-3" onClick={() => setAuditV3Sections(s => ({...s, working: !s.working}))}>
                        <CheckCircle className="w-4 h-4 text-[#1BB594] flex-shrink-0" />
                        <span className="text-sm font-bold text-slate-700">What's working</span>
                        <span className="text-xs text-[#1BB594] font-semibold">{auditPassed.length}</span>
                        {auditV3Sections.working ? <ChevronUp className="w-4 h-4 text-slate-400 ml-auto" /> : <ChevronDown className="w-4 h-4 text-slate-400 ml-auto" />}
                      </button>
                      {auditV3Sections.working && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 pl-6">
                          {auditPassed.map(c => (
                            <div key={c.id} className="flex items-center gap-2 py-1">
                              <CheckCircle className="w-3 h-3 text-green-400 flex-shrink-0" />
                              <span className="text-xs text-slate-500">{c.user_title}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── Progress comparison ── */}
                  {auditV3.comparison && (auditV3.comparison.improved?.length > 0 || auditV3.comparison.regressed?.length > 0) && (
                    <div className="mt-6 pt-6 border-t border-slate-200">
                      <button className="flex items-center gap-2 w-full text-left" onClick={() => setAuditV3Sections(s => ({...s, progress: !s.progress}))}>
                        <TrendingUp className="w-4 h-4 text-[#1BB594]" />
                        <span className="text-sm font-bold text-slate-700">
                          Progress since {new Date(auditV3.comparison.prev_date).toLocaleDateString(dateLocale)}
                        </span>
                        {auditV3Sections.progress ? <ChevronUp className="w-4 h-4 text-slate-400 ml-auto" /> : <ChevronDown className="w-4 h-4 text-slate-400 ml-auto" />}
                      </button>
                      {auditV3Sections.progress && (
                        <div className="mt-3 pl-6">
                          <div className="flex gap-4 flex-wrap mb-3">
                            {auditV3.comparison.improved?.length > 0 && (
                              <span className="text-xs px-2 py-1 bg-green-50 text-green-700 font-semibold">
                                +{auditV3.comparison.improved.length} fixed
                              </span>
                            )}
                            {auditV3.comparison.regressed?.length > 0 && (
                              <span className="text-xs px-2 py-1 bg-red-50 text-red-700 font-semibold">
                                {auditV3.comparison.regressed.length} regressed
                              </span>
                            )}
                            <span className="text-xs text-slate-400">
                              Previous score: {auditV3.comparison.prev_score}%
                            </span>
                          </div>
                          {auditV3.comparison.improved?.length > 0 && (
                            <div className="mb-2">
                              <p className="text-[10px] uppercase tracking-wider text-green-600 font-bold mb-1">Fixed</p>
                              <div className="space-y-1">
                                {auditV3.comparison.improved.map(id => {
                                  const check = auditChecks.find(c => c.id === id);
                                  return check ? (
                                    <div key={id} className="flex items-center gap-2">
                                      <ArrowUpRight className="w-3 h-3 text-green-500" />
                                      <span className="text-xs text-slate-600">{check.user_title}</span>
                                    </div>
                                  ) : null;
                                })}
                              </div>
                            </div>
                          )}
                          {auditV3.comparison.regressed?.length > 0 && (
                            <div>
                              <p className="text-[10px] uppercase tracking-wider text-red-600 font-bold mb-1">Regressed</p>
                              <div className="space-y-1">
                                {auditV3.comparison.regressed.map(id => {
                                  const check = auditChecks.find(c => c.id === id);
                                  return check ? (
                                    <div key={id} className="flex items-center gap-2">
                                      <ArrowDownRight className="w-3 h-3 text-red-500" />
                                      <span className="text-xs text-slate-600">{check.user_title}</span>
                                    </div>
                                  ) : null;
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : geoAudit ? (
                /* Old audit v1 fallback */
                <div>
                  <div className="flex items-center gap-4 mb-6">
                    <div className="text-5xl font-black text-[#1BB594]">{geoAudit.overall_score}<span className="text-2xl text-slate-400">/100</span></div>
                    <p className="text-sm text-slate-500">{t('geo.общий_балл')}</p>
                  </div>
                  {geoAudit.criteria?.length > 0 && (
                    <div className="space-y-3 mb-6">
                      {geoAudit.criteria.map((c, idx) => (
                        <div key={idx} className="p-4 bg-slate-50 border border-slate-200">
                          <div className="flex justify-between items-center mb-2">
                            <p className="font-semibold text-slate-900">{c.name}</p>
                            <div className="flex items-center gap-2">
                              <span className={`text-[10px] px-1.5 py-0.5 font-bold uppercase ${c.status === 'critical' ? 'bg-red-100 text-red-700' : c.status === 'warning' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'}`}>
                                {c.status === 'critical' ? t('geo.критично') : c.status === 'warning' ? t('geo.внимание') : t('geo.ок')}
                              </span>
                              <span className="text-lg font-black text-[#1BB594]">{c.score}<span className="text-xs text-slate-400">/{c.max_score || 10}</span></span>
                            </div>
                          </div>
                          {c.finding && <p className="text-sm text-slate-600 mb-1">{c.finding}</p>}
                          {c.recommendation && <p className="text-xs text-[#1BB594]">{c.recommendation}</p>}
                        </div>
                      ))}
                    </div>
                  )}
                  {geoAudit.top_3_actions?.length > 0 && (
                    <div>
                      <h3 className="text-lg font-bold text-slate-900 mb-3">{t('geo.топ_3_действия')}</h3>
                      <div className="space-y-2">
                        {geoAudit.top_3_actions.map((action, idx) => (
                          <div key={idx} className="flex items-start gap-3 p-3 bg-emerald-50 border border-emerald-200">
                            <span className="text-lg font-black text-[#1BB594] flex-shrink-0">{idx + 1}</span>
                            <p className="text-sm text-slate-900">{typeof action === 'string' ? action : action.action || action.description || JSON.stringify(action)}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-slate-500 text-sm">Run the audit to check your site's readiness for search engines and AI.</p>
              )}
            </div>

            {/* ─── History Calendar ─── */}
            {history.length > 0 && (
              <HistoryCalendar history={history} onSelectSnapshot={(snapshot, date) => console.log('Selected:', date, snapshot)} />
            )}

            {/* ─── Dynamics Chart ─── */}
            {chartData.length > 0 && (
              <div className="bg-white border border-[#E2DDD8] p-6 sm:p-8" data-testid="history-chart">
                <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
                  <h2 className="text-2xl sm:text-3xl tracking-tight font-bold text-[#2F2E2F]">{t('geo.динамика')}</h2>
                  <div className="flex gap-4 flex-wrap">
                    <div className="flex gap-2">
                      <button onClick={() => setChartMetric('visibility')} className={`rounded-none font-bold tracking-widest uppercase text-xs px-4 py-2 border transition-colors ${chartMetric === 'visibility' ? 'bg-[#1BB594] text-white border-[#1BB594]' : 'bg-white text-slate-700 border-[#E2DDD8] hover:bg-slate-50'}`}>
                        <TrendingUp className="w-4 h-4 inline mr-1" /> {t('geo.видимость')}
                      </button>
                      <button onClick={() => setChartMetric('citation')} className={`rounded-none font-bold tracking-widest uppercase text-xs px-4 py-2 border transition-colors ${chartMetric === 'citation' ? 'bg-[#1BB594] text-white border-[#1BB594]' : 'bg-white text-slate-700 border-[#E2DDD8] hover:bg-slate-50'}`}>
                        <LinkIcon className="w-4 h-4 inline mr-1" /> {t('geo.цитируемость')}
                      </button>
                    </div>
                    <div className="flex gap-2">
                      {TIME_FILTERS.map(filter => (
                        <button key={filter.days} onClick={() => setTimeFilter(filter.days)} className={`rounded-none font-bold tracking-widest uppercase text-xs px-3 py-2 border transition-colors ${timeFilter === filter.days ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-[#E2DDD8] hover:bg-slate-50'}`}>
                          {t('geo.' + filter.labelKey)}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={chartData} accessibilityLayer>
                    <CartesianGrid strokeDasharray="0" stroke="#E2DDD8" opacity={0.1} />
                    <XAxis dataKey="date" stroke="#64748B" style={{ fontSize: '12px' }} />
                    <YAxis stroke="#64748B" style={{ fontSize: '12px' }} />
                    <Tooltip contentStyle={{ background: '#fff', border: '1px solid #E2DDD8', borderRadius: 0, boxShadow: 'none' }} />
                    <Legend />
                    {Object.keys(AI_ENGINE_COLORS).filter(e => engines[e]).map(engine => (
                      <Line key={engine} type="monotone" dataKey={chartMetric === 'visibility' ? engine : `${engine}_citation`} name={engine} stroke={AI_ENGINE_COLORS[engine]} strokeWidth={2} dot={{ r: 3, fill: AI_ENGINE_COLORS[engine] }} connectNulls />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default GeoMonitorDetails;
